import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import ServiceToggle from './ServiceToggle';
import SettingsHeader from './SettingsHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { 
  User, Building2, CreditCard, Save, CheckCircle, XCircle, Clock, 
  AlertCircle, Loader2, Phone, Mail, MapPin, Calendar, Sliders,
  Eye, FileText, Shield, ChevronDown, ChevronUp, Palette, Activity,
  Download, Filter, Search, Key, Copy, Trash2, Plus, AlertTriangle,
  Users, Package, Lock, Sparkles, UserCheck, Webhook, Plug,
  ChevronLeft, ChevronRight, X, Check, Settings, Code
} from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../contexts/SettingsContext';
import apiV2 from '../utils/apiV2';
import api from '../utils/api';
import { BACKEND_URL, PRODUCTION_BACKEND_URL } from '../config/api';
import { TIMEZONES } from '../utils/timezones';

const SettingsTabEnhanced = ({ activeSubView }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { dateFormat, timeFormat, updateDateFormat, updateTimeFormat } = useSettings();
  
  // Check if user is a team member (limited access)
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamMemberData, setTeamMemberData] = useState(null);
  
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    setIsTeamMember(userType === 'teamMember');
    
    // If team member, get their info from token
    if (userType === 'teamMember') {
      const token = localStorage.getItem('jwtToken');
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const decoded = JSON.parse(window.atob(base64));
          setTeamMemberData({
            username: decoded.username,
            teamId: decoded.teamId,
            role: decoded.role
          });
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
    }
  }, []);

  // Load team-member self record on demand
  useEffect(() => {
    if (!isTeamMember) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiV2.get('/team-members/me');
        if (cancelled) return;
        const tm = res.data || {};
        setTeamMemberSelf(tm);
        setSelfDraft({
          name: tm.name || '',
          email: tm.email || '',
          phone: tm.phone || '',
        });
      } catch (err) {
        console.error('Failed to load self profile:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [isTeamMember]);
  
  // Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    city: '',
    timezone: 'America/Chicago',
    country: 'USA',
    companyWebsite: ''
  });
  
  // Waiver data
  const [waiverEnabled, setWaiverEnabled] = useState(false);
  const [waiverText, setWaiverText] = useState('');
  const [showWaiverPreview, setShowWaiverPreview] = useState(false);
  const [signedWaivers, setSignedWaivers] = useState([]);
  const [expandedWaivers, setExpandedWaivers] = useState(false);
  const [waiverPage, setWaiverPage] = useState(1);
  const waiversPerPage = 10;
  const [selectedWaiverDetails, setSelectedWaiverDetails] = useState(null);
  const [showWaiverDetailModal, setShowWaiverDetailModal] = useState(false);
  
  // Waiver Search
  const [waiverSearchQuery, setWaiverSearchQuery] = useState('');
  const [waiverSearchResults, setWaiverSearchResults] = useState([]);
  const [searchingWaivers, setSearchingWaivers] = useState(false);
  const [showWaiverSearch, setShowWaiverSearch] = useState(false);
  
  // Account management modals
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState(null);
  
  // Check-In System data
  const [checkInEnabled, setCheckInEnabled] = useState(false);
  const [checkInPin, setCheckInPin] = useState('0000');
  
  // Email Preferences
  const [reminderEmailsEnabled, setReminderEmailsEnabled] = useState(true);
  const [thankYouEmailsEnabled, setThankYouEmailsEnabled] = useState(true);
  
  // Subscription data
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Activity Log data
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotalPages, setActivityTotalPages] = useState(1);
  const [activityFilters, setActivityFilters] = useState({
    action: '',
    resourceType: '',
    startDate: '',
    endDate: ''
  });
  const [showActivityFilters, setShowActivityFilters] = useState(false);
  
  // Branding data
  const [uploading, setUploading] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');
  const [selectedColor, setSelectedColor] = useState('#2563EB');
  
  // Locations data
  const [locations, setLocations] = useState([]);
  
  // API Keys data
  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [showGeneratedKeyModal, setShowGeneratedKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiration, setNewKeyExpiration] = useState('never');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [keyIsLive, setKeyIsLive] = useState(false);
  const [checkingKeyStatus, setCheckingKeyStatus] = useState(false);
  const [testingKey, setTestingKey] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [expandedKeyId, setExpandedKeyId] = useState(null);
  const [keyUsageStats, setKeyUsageStats] = useState({});
  
  // Individual endpoint test states
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [endpointTestStatus, setEndpointTestStatus] = useState({
    bookings: 'idle',    // idle | testing | success | error
    team: 'idle',
    assets: 'idle',
    services: 'idle',
    waivers: 'idle'
  });
  const [endpointResponses, setEndpointResponses] = useState({
    bookings: null,
    team: null,
    assets: null,
    services: null,
    waivers: null
  });
  
  // Store interval ref for cleanup
  const [pollInterval, setPollInterval] = useState(null);
  
  // Preferences temp state (before save)
  const [tempDateFormat, setTempDateFormat] = useState('MM/DD/YYYY');
  const [tempTimeFormat, setTempTimeFormat] = useState('12h');
  
  // Calendar tab state
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Webhooks tab state
  const [webhooks, setWebhooks] = useState([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [showNewWebhookModal, setShowNewWebhookModal] = useState(false);
  const [newWebhookUrl, setNewWebhookUrl] = useState('');
  const [newWebhookEvents, setNewWebhookEvents] = useState({
    'booking.created': true,
    'booking.updated': false,
    'booking.cancelled': false,
  });
  const [testingWebhook, setTestingWebhook] = useState(null);

  // Password reset state for team members - MUST be declared before any early returns
  const [requestingPasswordReset, setRequestingPasswordReset] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);

  // Team-member self profile (live editable record loaded from /team-members/me)
  const [teamMemberSelf, setTeamMemberSelf] = useState(null);
  const [selfDraft, setSelfDraft] = useState({ name: '', email: '', phone: '' });
  const [selfPassword, setSelfPassword] = useState({ next: '', confirm: '' });
  const [showSelfPasswordForm, setShowSelfPasswordForm] = useState(false);

  useEffect(() => {
    fetchProfileData();
    checkPendingEmailChange();
    
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'canceled') {
      toast.error('Payment was canceled');
      window.history.replaceState({}, '', '/admin');
    }
  }, []);
  
  const checkPendingEmailChange = async () => {
    try {
      const response = await apiV2.get('/auth/account/pending-email-change');
      if (response.data.hasPendingChange) {
        setPendingEmailChange({
          email: response.data.pendingEmail,
          requestedAt: response.data.requestedAt
        });
      } else {
        setPendingEmailChange(null);
      }
    } catch (error) {
      console.error('Error checking pending email change:', error);
    }
  };

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      
      // Fetch organization data
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId;
      
      
      if (orgId) {
        // Use v2 auth endpoint which returns org data from PostgreSQL
        const orgResponse = await apiV2.get(`/auth/organizations/me`);
        const orgDataFromApi = orgResponse.data;
        setOrgData({
          ...orgDataFromApi,
          id: orgDataFromApi.id || orgId,  // Ensure id is explicitly set
          emergencyPhone: orgDataFromApi.emergencyPhoneForTickets || orgDataFromApi.emergencyPhone || '',
          country: orgDataFromApi.country || 'USA',
          companyWebsite: orgDataFromApi.companyWebsite || ''
        });
        
        // Load waiver settings from API
        setWaiverText(orgDataFromApi.customWaiverText || '');
        setWaiverEnabled(orgDataFromApi.waiversEnabled === true);
        
        // Load check-in settings
        setCheckInEnabled(orgDataFromApi.checkInEnabled === true);
        setCheckInPin(orgDataFromApi.checkInPin || '0000');
        
        // Load email preferences (default to true)
        setReminderEmailsEnabled(orgDataFromApi.reminderEmailsEnabled !== false);
        setThankYouEmailsEnabled(orgDataFromApi.thankYouEmailsEnabled !== false);
        
      }
      
      // Fetch subscription status
      try {
        const subResponse = await apiV2.get('/subscriptions/my-status');
        setSubscriptionStatus(subResponse.data);
      } catch (err) {
        console.error('Error fetching subscription:', err);
      }
      
      // Fetch signed waivers
      loadSignedWaivers();
      
      // Fetch locations
      fetchLocations();
      
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await apiV2.get('/platform/locations');
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const loadSignedWaivers = async () => {
    try {
      const response = await apiV2.get('/bookings');
      const waivers = response.data
        .filter(t => t.waiverSigned)
        .map(t => ({
          confirmationCode: t.confirmationCode || t.id.slice(0, 8),
          customerName: t.waiverRecord?.signatureName || t.customerName,
          method: t.waiverRecord?.verificationMethod || 'unknown',
          signedAt: t.waiverSignedAt,
          trip: t  // CRITICAL: Include full trip object for detailed view
        }));
      setSignedWaivers(waivers);
    } catch (error) {
      console.error('Error loading waivers:', error);
    }
  };

  const getDefaultWaiver = () => {
    return '';  // No default text - operators must provide their own
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      // Validate org name is not empty
      if (!orgData.name || orgData.name.trim() === '') {
        toast.error('Organization name is required');
        setSaving(false);
        return;
      }
      
      const updateData = {
        name: orgData.name.trim(),
        ownerName: orgData.ownerName,
        ownerEmail: orgData.ownerEmail,
        ownerPhone: orgData.ownerPhone,
        city: orgData.city,
        timezone: orgData.timezone,
        country: orgData.country,
        companyWebsite: orgData.companyWebsite
      };
      
      // Get org ID from multiple sources (same fix as waivers)
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      
      if (!orgId) {
        throw new Error('No organization ID found');
      }
      
      const response = await apiV2.patch(`/auth/organizations/me`, updateData);
      
      // Save to localStorage as well
      localStorage.setItem('organizationName', orgData.name);
      localStorage.setItem('orgTimezone', orgData.timezone);
      localStorage.setItem('orgCountry', orgData.country);
      
      toast.success('Profile saved successfully!');
      
      // Refresh data to confirm changes
      await fetchProfileData();
    } catch (error) {
      console.error('Error saving profile:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleWaiver = async (newState) => {
    // If turning OFF, show confirmation
    if (!newState && waiverEnabled) {
      if (!window.confirm('Are you sure you want to disable waivers? Customers will NOT be required to sign waivers before viewing trip details.')) {
        return; // User canceled
      }
    }
    
    // Update state
    setWaiverEnabled(newState);
    
    // Auto-save toggle state
    try {
      // Get org ID from multiple sources
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      
      if (!orgId) {
        toast.error('Organization ID not found. Please refresh the page.');
        setWaiverEnabled(!newState); // Revert state
        return;
      }
      
      await apiV2.patch(`/auth/organizations/me`, {
        waiversEnabled: newState
      });
      
      toast.success(newState ? 'Waivers enabled' : 'Waivers disabled');
      
      // Refresh data
      fetchProfileData();
    } catch (error) {
      console.error('Error toggling waiver:', error);
      toast.error('Failed to save waiver state');
      setWaiverEnabled(!newState); // Revert state
    }
  };

  const handleSaveWaivers = async () => {
    // Validate waiver text is not empty
    if (!waiverText.trim()) {
      toast.error('Waiver text is required. BookingFlows does not provide legal templates.');
      return;
    }
    
    if (waiverText.trim().length < 100) {
      toast.error('Waiver text must be at least 100 characters. Consult a lawyer for proper waiver language.');
      return;
    }
    
    try {
      setSaving(true);
      
      const oldText = localStorage.getItem('orgWaiverText') || '';
      
      // Get org ID from multiple sources (PRIMARY: direct organizationId in localStorage)
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      
      if (!orgId) {
        toast.error('Organization ID not found. Please refresh the page.');
        console.error('ERROR: No organization ID found!');
        return;
      }
      
      // Save to database
      const response = await apiV2.patch(`/auth/organizations/me`, {
        waiversEnabled: waiverEnabled,
        customWaiverText: waiverText
      });
      
      // Also save to localStorage for backward compatibility
      localStorage.setItem('waiversEnabled', waiverEnabled.toString());
      localStorage.setItem('orgWaiverText', waiverText);
      
      // Log changes for liability tracking
      if (oldText !== waiverText) {
        try {
          await apiV2.post('/settings/log-waiver-change', {
            textChanged: true,
            textLength: { old: oldText.length, new: waiverText.length }
          });
        } catch (err) {
          console.error('Error logging waiver change:', err);
        }
      }
      
      toast.success('Waiver settings saved!');
      
      // Refresh organization data to show saved waiver
      await fetchProfileData();
    } catch (error) {
      console.error('Error saving waivers:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please refresh the page.');
      setProcessing(false);
      return;
    }

    setProcessing(true);

    try {
      const response = await apiV2.get(`/subscriptions/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        toast.success('🎉 Subscription activated!');
        fetchProfileData();
        setProcessing(false);
        window.history.replaceState({}, '', '/admin');
        return;
      } else if (response.data.status === 'expired') {

  const handleSaveCheckIn = async () => {
    try {
      setSaving(true);
      
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      if (!orgId) {
        toast.error('Organization ID not found. Please refresh the page.');
        return;
      }
      
      // Validate PIN is 4 digits
      if (checkInEnabled && (!/^\d{4}$/.test(checkInPin))) {
        toast.error('PIN must be exactly 4 digits');
        return;
      }
      
      // Save to database
      await apiV2.patch(`/auth/organizations/me`, {
        checkInEnabled: checkInEnabled,
        checkInPin: checkInPin
      });
      
      toast.success('Check-in settings saved!');
      
    } catch (error) {
      console.error('Error saving check-in settings:', error);
      toast.error(error.response?.data?.detail || 'Failed to save check-in settings');
    } finally {
      setSaving(false);
    }
  };

        toast.error('Payment session expired. Please try again.');
        setProcessing(false);
        window.history.replaceState({}, '', '/admin');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error verifying payment. Please refresh the page.');
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setProcessing(true);
      const originUrl = window.location.origin;
      
      const response = await apiV2.post('/subscriptions/create-checkout', null, {
        params: {
          package_id: 'starter',
          origin_url: originUrl
        }
      });
      
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to start checkout. Please try again.');
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }

    try {
      setProcessing(true);
      await apiV2.post('/subscriptions/cancel');
      toast.success('Subscription canceled');
      fetchProfileData();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 text-xs font-medium"><CheckCircle className="w-3 h-3 mr-1" /> Active</Badge>;
      case 'canceled':
        return <Badge className="bg-gray-100 text-gray-700 text-xs font-medium"><XCircle className="w-3 h-3 mr-1" /> Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-amber-100 text-amber-700 text-xs font-medium"><AlertCircle className="w-3 h-3 mr-1" /> Past Due</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-600 text-xs font-medium"><Clock className="w-3 h-3 mr-1" /> Inactive</Badge>;
    }
  };


  // Activity Log functions
  const fetchActivities = async () => {
    if (activeSubView !== 'settings-activity') return;
    
    try {
      setLoadingActivities(true);
      
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      
      const params = new URLSearchParams({
        limit: '20',
        offset: ((activityPage - 1) * 20).toString()
      });
      
      if (activityFilters.action) params.append('action', activityFilters.action);
      if (activityFilters.resourceType) params.append('entity_type', activityFilters.resourceType);
      // Team members only see their own actions
      if (isTeamMember) params.append('scope', 'mine');
      
      // Use v2 PostgreSQL endpoint
      const response = await apiV2.get(`/organizations/${orgId}/settings/activity-logs?${params.toString()}`);
      setActivities(response.data.logs || []);
      setActivityTotalPages(Math.ceil((response.data.total || 0) / 20));
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to load activity log');
    } finally {
      setLoadingActivities(false);
    }
  };

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportRange, setExportRange] = useState(() => {
    const today = new Date();
    const ninetyDaysAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000);
    return {
      start: ninetyDaysAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0],
    };
  });
  const [exporting, setExporting] = useState(false);

  const handleExportActivities = () => {
    setShowExportModal(true);
  };

  const handleConfirmExport = async () => {
    if (!exportRange.start || !exportRange.end) {
      toast.error('Pick a start and end date');
      return;
    }
    if (new Date(exportRange.start) > new Date(exportRange.end)) {
      toast.error('Start date must be before end date');
      return;
    }
    try {
      setExporting(true);
      const orgId = localStorage.getItem('organizationId');
      const params = new URLSearchParams({
        start_date: exportRange.start,
        end_date: exportRange.end,
      });
      if (activityFilters.action) params.append('action', activityFilters.action);
      if (activityFilters.resourceType) params.append('entity_type', activityFilters.resourceType);
      if (isTeamMember) params.append('scope', 'mine');

      const response = await apiV2.get(
        `/organizations/${orgId}/settings/activity-logs/export?${params}`,
        { responseType: 'blob' }
      );
      const rowCount = response.headers['x-row-count'];

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `activity-log_${exportRange.start}_to_${exportRange.end}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${rowCount || ''} log entries`);
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export activity log');
    } finally {
      setExporting(false);
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      created: 'bg-green-100 text-green-800',
      updated: 'bg-blue-100 text-blue-800',
      deleted: 'bg-red-100 text-red-800',
      assigned: 'bg-purple-100 text-purple-800'
    };
    return colors[action] || 'bg-gray-100 text-gray-800';
  };

  // Branding functions
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }

    try {
      setUploading(true);
      
      // Get org ID from multiple sources (same fix as profile)
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      if (!orgId) {
        throw new Error('No organization ID found');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await apiV2.post(`/auth/organizations/me/upload-logo`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const logoUrl = response.data.logoUrl;
      
      setLogoPreview(logoUrl);
      setOrgData(prev => ({ ...prev, logoUrl }));
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const handleSaveBranding = async () => {
    try {
      setSaving(true);
      
      // Get org ID from multiple sources (same fix as profile)
      const orgIdFromStorage = localStorage.getItem('organizationId');
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const orgId = orgIdFromStorage || userData.organizationId || orgData.id;
      
      if (!orgId) {
        throw new Error('No organization ID found');
      }
      
      const updateData = {
        primaryColor: selectedColor
      };
      
      // Include logoUrl if we have one
      if (logoPreview) {
        updateData.logoUrl = logoPreview;
      }
      
      const response = await apiV2.patch(`/auth/organizations/me`, updateData);
      
      setOrgData(prev => ({ ...prev, primaryColor: selectedColor, logoUrl: logoPreview }));
      toast.success('Branding saved successfully!');
      
      // Refresh data to confirm
      await fetchProfileData();
    } catch (error) {
      console.error('Error saving branding:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };


  // API Keys functions
  const fetchApiKeys = async () => {
    if (activeSubView !== 'settings-api-keys') return;
    
    try {
      setLoadingKeys(true);
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      // Use v2 PostgreSQL endpoint
      const response = await apiV2.get(`/organizations/${orgId}/settings/api-keys`);
      setApiKeys(response.data.apiKeys || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setLoadingKeys(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }
    
    // Check if they already have 5 keys
    if (apiKeys.length >= 5) {
      toast.error('Maximum of 5 API keys allowed. Please revoke an existing key first.');
      return;
    }

    try {
      setSaving(true);
      
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      
      // Calculate expiration days
      let expiresInDays = null;
      if (newKeyExpiration !== 'never') {
        expiresInDays = parseInt(newKeyExpiration);
      }
      
      // Use v2 PostgreSQL endpoint
      const response = await apiV2.post(`/organizations/${orgId}/settings/api-keys`, {
        name: newKeyName,
        expires_in_days: expiresInDays,
        permissions: ["read:bookings", "read:services", "read:assets", "read:team", "read:waivers"]
      });
      
      setGeneratedKey({
        ...response.data.apiKey,
        key: response.data.key
      });
      setKeyIsLive(false);
      setNewKeyName('');
      setNewKeyExpiration('never');
      setShowNewKeyModal(false);
      setShowGeneratedKeyModal(true);
      
      // Start checking if key gets used
      startKeyStatusCheck(response.data.id);
      
      fetchApiKeys();
    } catch (error) {
      console.error('Error generating API key:', error);
      toast.error('Failed to generate API key');
    } finally {
      setSaving(false);
    }
  };
  
  const startKeyStatusCheck = (keyId) => {
    // Clear any existing interval
    if (pollInterval) {
      clearInterval(pollInterval);
    }
    
    setCheckingKeyStatus(true);
    let attempts = 0;
    const maxAttempts = 10; // Reduced from 20 (30 seconds total)
    
    const interval = setInterval(async () => {
      attempts++;
      
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setPollInterval(null);
        setCheckingKeyStatus(false);
        return;
      }
      
      try {
        const response = await apiV2.get(`/api-keys/${keyId}/usage-stats`);
        if (response.data.totalRequestsToday > 0) {
          setKeyIsLive(true);
          setCheckingKeyStatus(false);
          clearInterval(interval);
          setPollInterval(null);
          toast.success('API key is live!');
        }
      } catch (error) {
        // Ignore errors during polling
      }
    }, 3000);
    
    setPollInterval(interval);
  };
  
  const testEndpoint = async (endpointName, path) => {
    if (!generatedKey) return;
    
    setEndpointTestStatus(prev => ({ ...prev, [endpointName]: 'testing' }));
    setEndpointResponses(prev => ({ ...prev, [endpointName]: null }));
    
    try {
      const url = `${BACKEND_URL}${path}?api_key=${generatedKey.key}`;
      const response = await fetch(url);
      
      // Read response text first, then parse
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        data = { error: 'Failed to parse response', raw: responseText };
      }
      
      if (response.ok) {
        setEndpointTestStatus(prev => ({ ...prev, [endpointName]: 'success' }));
        setEndpointResponses(prev => ({ ...prev, [endpointName]: data }));
        setKeyIsLive(true);
        toast.success(`${endpointName} endpoint working!`);
      } else {
        setEndpointTestStatus(prev => ({ ...prev, [endpointName]: 'error' }));
        setEndpointResponses(prev => ({ ...prev, [endpointName]: data }));
        toast.error(`${endpointName} test failed: ${response.status}`);
      }
    } catch (error) {
      setEndpointTestStatus(prev => ({ ...prev, [endpointName]: 'error' }));
      setEndpointResponses(prev => ({ ...prev, [endpointName]: { error: error.message } }));
      toast.error(`${endpointName} test error: ${error.message}`);
    }
  };
  
  // Cleanup interval on component unmount or modal close
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  const handleRevokeKey = async (keyId, keyName) => {
    if (!window.confirm(`Are you sure you want to revoke "${keyName}"? This cannot be undone.`)) {
      return;
    }

    try {
      const orgId = localStorage.getItem('organizationId');
      // Use v2 PostgreSQL endpoint
      await apiV2.delete(`/organizations/${orgId}/settings/api-keys/${keyId}`);
      toast.success('API key revoked');
      fetchApiKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  
  const handleTestKey = async (keyId) => {
    try {
      setTestingKey(keyId);
      const response = await apiV2.post(`/api-keys/${keyId}/test`);
      setTestResult(response.data);
      
      if (response.data.success) {
        toast.success('API key is working!');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error testing key:', error);
      toast.error('Failed to test API key');
      setTestResult({ success: false, message: 'Error testing key' });
    } finally {
      setTestingKey(null);
    }
  };
  
  const fetchKeyUsageStats = async (keyId) => {
    try {
      const response = await apiV2.get(`/api-keys/${keyId}/usage-stats`);
      setKeyUsageStats(prev => ({
        ...prev,
        [keyId]: response.data
      }));
    } catch (error) {
      console.error('Error fetching usage stats:', error);
    }
  };
  
  const toggleKeyExpanded = (keyId) => {
    if (expandedKeyId === keyId) {
      setExpandedKeyId(null);
    } else {
      setExpandedKeyId(keyId);
      fetchKeyUsageStats(keyId);
    }
  };

  // useEffect for API keys
  useEffect(() => {
    if (activeSubView === 'settings-api-keys') {
      fetchApiKeys();
    }
  }, [activeSubView]);


  // useEffect for activity log
  useEffect(() => {
    if (activeSubView === 'settings-activity') {
      fetchActivities();
    }
  }, [activityPage, activeSubView]);

  // useEffect for branding
  useEffect(() => {
    if (activeSubView === 'settings-branding') {
      setLogoPreview(orgData.logoUrl || '');
      setSelectedColor(orgData.primaryColor || '#2563EB');
    }
  }, [activeSubView, orgData.logoUrl, orgData.primaryColor]);
  
  // useEffect for preferences
  useEffect(() => {
    setTempDateFormat(dateFormat);
    setTempTimeFormat(timeFormat);
  }, [dateFormat, timeFormat]);
  
  // useEffect to reload waivers when tab opens
  useEffect(() => {
    if (activeSubView === 'settings-waivers') {
      fetchProfileData();
    }
  }, [activeSubView]);
  
  // Waiver search function
  const searchWaivers = async (query) => {
    if (!query || query.length < 2) {
      setWaiverSearchResults([]);
      return;
    }
    
    try {
      setSearchingWaivers(true);
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      const response = await apiV2.get(`/organizations/${orgId}/waivers/search`, {
        params: { q: query, limit: 50 }
      });
      setWaiverSearchResults(response.data.waivers || []);
    } catch (error) {
      console.error('Error searching waivers:', error);
      toast.error('Failed to search waivers');
      setWaiverSearchResults([]);
    } finally {
      setSearchingWaivers(false);
    }
  };
  
  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (waiverSearchQuery.length >= 2) {
        searchWaivers(waiverSearchQuery);
      } else {
        setWaiverSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [waiverSearchQuery]);
  
  // useEffect for webhooks
  useEffect(() => {
    if (activeSubView === 'settings-webhooks') {
      fetchWebhooks();
    }
  }, [activeSubView]);
  
  // Webhook functions
  const fetchWebhooks = async () => {
    try {
      setLoadingWebhooks(true);
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      // Use v2 PostgreSQL endpoint
      const response = await apiV2.get(`/organizations/${orgId}/settings/webhooks`);
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
      toast.error('Failed to load webhooks');
    } finally {
      setLoadingWebhooks(false);
    }
  };
  
  const handleCreateWebhook = async () => {
    if (!newWebhookUrl.trim()) {
      toast.error('Please enter a webhook URL');
      return;
    }
    
    if (!newWebhookUrl.startsWith('http://') && !newWebhookUrl.startsWith('https://')) {
      toast.error('Webhook URL must start with http:// or https://');
      return;
    }
    
    const selectedEvents = Object.keys(newWebhookEvents).filter(event => newWebhookEvents[event]);
    if (selectedEvents.length === 0) {
      toast.error('Please select at least one event to trigger');
      return;
    }
    
    try {
      setSaving(true);
      const orgId = localStorage.getItem('organizationId');
      if (!orgId) {
        toast.error('Organization not found');
        return;
      }
      // Use v2 PostgreSQL endpoint
      await apiV2.post(`/organizations/${orgId}/settings/webhooks`, {
        name: `Webhook ${webhooks.length + 1}`,
        url: newWebhookUrl,
        events: selectedEvents
      });
      
      toast.success('Webhook created!');
      setNewWebhookUrl('');
      setNewWebhookEvents({
        'booking.created': true,
        'booking.updated': false,
        'booking.cancelled': false,
      });
      setShowNewWebhookModal(false);
      fetchWebhooks();
    } catch (error) {
      console.error('Error creating webhook:', error);
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteWebhook = async (webhookId) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }
    
    try {
      const orgId = localStorage.getItem('organizationId');
      // Use v2 PostgreSQL endpoint
      await apiV2.delete(`/organizations/${orgId}/settings/webhooks/${webhookId}`);
      toast.success('Webhook deleted');
      fetchWebhooks();
    } catch (error) {
      console.error('Error deleting webhook:', error);
      toast.error('Failed to delete webhook');
    }
  };
  
  const handleTestWebhook = async (webhookId) => {
    try {
      setTestingWebhook(webhookId);
      const orgId = localStorage.getItem('organizationId');
      
      const response = await apiV2.post(`/organizations/${orgId}/settings/webhooks/${webhookId}/test`);
      
      const status = response.data.status_code;
      if (response.data.success) {
        toast.success(`Webhook delivered — your endpoint replied ${status}`);
      } else if (status >= 400) {
        // Our request succeeded, but the customer's endpoint returned an error
        toast.error(`Your endpoint responded ${status}. Check that the URL is reachable.`);
      } else {
        toast.error(response.data.message || 'Webhook test failed');
      }
    } catch (error) {
      console.error('Error testing webhook:', error);
      toast.error(error.response?.data?.detail || 'Could not reach the webhook endpoint');
    } finally {
      setTestingWebhook(null);
    }
  };
  
  const handleToggleWebhook = async (webhookId, currentStatus) => {
    try {
      const orgId = localStorage.getItem('organizationId');
      // Use v2 PostgreSQL endpoint
      await apiV2.patch(`/organizations/${orgId}/settings/webhooks/${webhookId}`, {
        is_active: !currentStatus
      });
      toast.success(currentStatus ? 'Webhook disabled' : 'Webhook enabled');
      fetchWebhooks();
    } catch (error) {
      console.error('Error toggling webhook:', error);
      toast.error('Failed to update webhook');
    }
  };
  
  // Calendar functions — smart routing: team member gets their own feed, admin gets org feed
  // isTeamMember and teamMemberData already declared as state at top of component
  const getCalendarFeedUrl = async () => {
    const baseUrl = PRODUCTION_BACKEND_URL.replace('http://', '').replace('https://', '');
    const tmId = teamMemberData?.teamId || teamMemberData?.id;
    if (isTeamMember && tmId) {
      // Team member: use their personal feed filtered to assigned trips
      const response = await apiV2.get(`/team-members/${tmId}/calendar-feed-token`);
      const token = response.data.token;
      return `webcal://${baseUrl}/api/v2/team-members/${tmId}/calendar-feed.ics?token=${token}`;
    } else {
      // Admin/owner: use org-wide feed
      const orgId = localStorage.getItem('organizationId');
      const response = await apiV2.get(`/auth/organizations/${orgId}/calendar-feed-token`);
      const token = response.data.token;
      return `webcal://${baseUrl}/api/organizations/${orgId}/calendar-feed.ics?token=${token}`;
    }
  };

  const handleCopyFeedUrl = async () => {
    try {
      const feedUrl = await getCalendarFeedUrl();
      navigator.clipboard.writeText(feedUrl);
      setCopySuccess(true);
      toast.success('Calendar feed URL copied!');
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (error) {
      console.error('Error copying feed URL:', error);
      toast.error('Failed to copy feed URL');
    }
  };
  
  const handleSubscribeToFeed = async () => {
    try {
      const feedUrl = await getCalendarFeedUrl();
      window.location.href = feedUrl;
      toast.success('Opening your calendar app...');
    } catch (error) {
      console.error('Error subscribing to feed:', error);
      toast.error('Failed to open calendar feed');
    }
  };
  
  // Preferences save handler
  const handleSavePreferences = async () => {
    updateDateFormat(tempDateFormat);
    updateTimeFormat(tempTimeFormat);
    toast.success('Preferences saved!');
  };


  const handleVerifyWaiverPin = async () => {
    // Simplified: Just log that waiver details were viewed (no PIN required)
    try {
      const orgId = localStorage.getItem('organizationId');
      await apiV2.post(`/organizations/${orgId}/log-waiver-view`, {
        tripId: selectedWaiverDetails?.confirmationCode || selectedWaiverDetails?.id
      });
    } catch (error) {
      console.error('Error logging waiver view:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const handleRequestPasswordReset = async () => {
    if (!teamMemberData?.username) {
      toast.error('Unable to find your email address');
      return;
    }
    
    try {
      setRequestingPasswordReset(true);
      
      const response = await apiV2.post('/team-members/request-password-reset', {
        email: teamMemberData.username,
        orgSlug: orgData.slug,
        teamMemberId: teamMemberData?.teamId
      });
      
      if (response.data.success) {
        setPasswordResetSent(true);
        toast.success('Password reset email sent! Check your inbox.');
      }
    } catch (error) {
      console.error('Error requesting password reset:', error);
      toast.error(error.response?.data?.detail || 'Failed to send password reset email');
    } finally {
      setRequestingPasswordReset(false);
    }
  };

  // Self-edit handler for team members
  const handleSelfProfileSave = async (changes) => {
    try {
      setSaving(true);
      const response = await apiV2.patch('/team-members/me', changes);
      if (response.data?.success) {
        toast.success('Profile updated');
        // Refresh local UI state from server response
        const tm = response.data.teamMember;
        setTeamMemberSelf((prev) => ({ ...prev, ...tm }));
      }
      return true;
    } catch (error) {
      console.error('Self profile save failed:', error);
      toast.error(error.response?.data?.detail || 'Could not update profile');
      return false;
    } finally {
      setSaving(false);
    }
  };
  
  // PROFILE TAB - Team Member View (Editable Self-Service)
  if (activeSubView === 'settings-profile' && isTeamMember) {
    const dirty =
      selfDraft.name !== (teamMemberSelf?.name || '') ||
      selfDraft.email !== (teamMemberSelf?.email || '') ||
      selfDraft.phone !== (teamMemberSelf?.phone || '');

    const onSaveProfile = async () => {
      const changes = {};
      if (selfDraft.name !== (teamMemberSelf?.name || '')) changes.name = selfDraft.name.trim();
      if (selfDraft.email !== (teamMemberSelf?.email || '')) changes.email = selfDraft.email.trim();
      if (selfDraft.phone !== (teamMemberSelf?.phone || '')) changes.phone = selfDraft.phone.trim();
      if (Object.keys(changes).length === 0) return;
      await handleSelfProfileSave(changes);
    };

    const onChangePassword = async () => {
      if (!selfPassword.next || selfPassword.next.length < 8) {
        toast.error('Password must be at least 8 characters');
        return;
      }
      if (selfPassword.next !== selfPassword.confirm) {
        toast.error('Passwords do not match');
        return;
      }
      const ok = await handleSelfProfileSave({ password: selfPassword.next });
      if (ok) {
        setSelfPassword({ next: '', confirm: '' });
        setShowSelfPasswordForm(false);
      }
    };

    return (
      <div className="space-y-4">
        <SettingsHeader
          title="My Profile"
          subtitle="Your personal account details."
        >
          <Button
            onClick={onSaveProfile}
            disabled={saving || !dirty}
            size="sm"
            style={{ backgroundColor: '#2563EB' }}
            className="h-9 hover:opacity-90 text-white"
            data-testid="self-profile-save"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Changes
          </Button>
        </SettingsHeader>

        {/* Editable personal details */}
        <div className="settings-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Personal Information</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Full Name</Label>
              <Input
                value={selfDraft.name}
                onChange={(e) => setSelfDraft((d) => ({ ...d, name: e.target.value }))}
                className="h-9 text-sm mt-1"
                placeholder="Your name"
                data-testid="self-name-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Email</Label>
              <Input
                type="email"
                value={selfDraft.email}
                onChange={(e) => setSelfDraft((d) => ({ ...d, email: e.target.value }))}
                className="h-9 text-sm mt-1"
                placeholder="you@example.com"
                data-testid="self-email-input"
              />
              <p className="text-xs text-gray-500 mt-1">Used for login and notifications</p>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Phone</Label>
              <Input
                value={selfDraft.phone}
                onChange={(e) => setSelfDraft((d) => ({ ...d, phone: e.target.value }))}
                className="h-9 text-sm mt-1"
                placeholder="+1 555 555 0123"
                data-testid="self-phone-input"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Role</Label>
              <Input
                value={teamMemberSelf?.roleName || 'Team Member'}
                disabled
                className="h-9 text-sm bg-gray-100 mt-1"
                data-testid="self-role"
              />
              <p className="text-xs text-gray-500 mt-1">Set by your admin</p>
            </div>
          </div>
        </div>

        {/* Organization context — read-only */}
        <div className="settings-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Organization</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-gray-500">Organization</Label>
              <Input
                value={orgData.name || ''}
                disabled
                className="h-9 text-sm bg-gray-100 mt-1"
                data-testid="self-org-name"
              />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Shortname (used for login)</Label>
              <Input
                value={orgData.slug || ''}
                disabled
                className="h-9 text-sm bg-gray-100 mt-1"
                data-testid="self-org-slug"
              />
            </div>
          </div>
        </div>

        {/* Password change — in-place */}
        <div className="settings-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-gray-600" />
              <h3 className="font-semibold text-sm">Password</h3>
            </div>
            {!showSelfPasswordForm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSelfPasswordForm(true)}
                className="h-8"
                data-testid="self-change-password-btn"
              >
                Change password
              </Button>
            )}
          </div>

          {!showSelfPasswordForm ? (
            <p className="text-sm text-gray-600">
              For your security, update your password regularly. Minimum 8 characters.
            </p>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500">New password</Label>
                  <Input
                    type="password"
                    value={selfPassword.next}
                    onChange={(e) => setSelfPassword((p) => ({ ...p, next: e.target.value }))}
                    className="h-9 text-sm mt-1"
                    placeholder="At least 8 characters"
                    data-testid="self-new-password"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Confirm password</Label>
                  <Input
                    type="password"
                    value={selfPassword.confirm}
                    onChange={(e) => setSelfPassword((p) => ({ ...p, confirm: e.target.value }))}
                    className="h-9 text-sm mt-1"
                    placeholder="Re-enter the new password"
                    data-testid="self-confirm-password"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={onChangePassword}
                  disabled={saving}
                  style={{ backgroundColor: '#2563EB' }}
                  className="h-9 hover:opacity-90 text-white"
                  data-testid="self-save-password"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Update password
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowSelfPasswordForm(false);
                    setSelfPassword({ next: '', confirm: '' });
                  }}
                  className="h-9"
                  data-testid="self-cancel-password"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // PROFILE TAB - Admin View (Full Access)
  if (activeSubView === 'settings-profile') {
    return (
      <>
        <div className="space-y-4">
          <SettingsHeader
            title="Profile"
            subtitle="Manage your organization and owner details."
          >
            <Button
              onClick={handleSaveProfile}
              disabled={saving}
              size="sm"
              style={{ backgroundColor: '#2563EB' }}
              className="hover:opacity-90 h-9 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-3.5 h-3.5 mr-1.5" />
                  Save
                </>
              )}
            </Button>
          </SettingsHeader>

          {/* Organization Section - Compact */}
          <div className="settings-card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-4 h-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Organization</h3>
            </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="field-label">Name <span className="text-red-500">*</span></Label>
              <Input
                value={orgData.name}
                onChange={(e) => setOrgData({...orgData, name: e.target.value})}
                placeholder="Austin Charter Tours"
                className={`h-9 text-sm ${!orgData.name || orgData.name.trim() === '' ? 'border-red-300 focus:ring-red-500' : ''}`}
              />
              {(!orgData.name || orgData.name.trim() === '') && (
                <p className="text-xs text-red-500 mt-1">Required</p>
              )}
            </div>
            
            <div>
              <Label className="field-label">Shortname</Label>
              <Input
                value={orgData.slug}
                disabled
                className="h-9 text-sm bg-gray-100"
              />
              <p className="text-xs text-gray-500 mt-1">Used in URLs</p>
            </div>
            
            <div className="col-span-2">
              <Label className="field-label">Company Website</Label>
              <div className="flex gap-2">
                <div className="flex-1 flex items-center border rounded-md overflow-hidden">
                  <div className="bg-gray-100 px-3 h-9 flex items-center border-r text-xs text-gray-600 font-mono">
                    https://
                  </div>
                  <Input
                    type="text"
                    value={orgData.companyWebsite || ''}
                    onChange={(e) => {
                      // Remove any http:// or https:// prefix if user types it
                      let value = e.target.value;
                      value = value.replace(/^https?:\/\//, '');
                      setOrgData({...orgData, companyWebsite: value});
                    }}
                    placeholder="www.yourcompany.com"
                    className="h-9 text-sm border-0 focus:ring-0 focus-visible:ring-0 shadow-none"
                  />
                </div>
                {orgData.companyWebsite && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Ensure URL has https:// prefix
                      let url = orgData.companyWebsite;
                      if (!url.startsWith('http://') && !url.startsWith('https://')) {
                        url = 'https://' + url;
                      }
                      window.open(url, '_blank');
                    }}
                    className="h-9"
                  >
                    Preview
                  </Button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Just enter your domain (e.g., www.yourcompany.com or yourcompany.io)
              </p>
            </div>
            
            <div>
              <Label className="field-label">Timezone</Label>
              <select
                value={orgData.timezone}
                onChange={(e) => setOrgData({...orgData, timezone: e.target.value})}
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {TIMEZONES.map(tz => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="field-label">Country / Region</Label>
              <select
                value={orgData.country || 'USA'}
                disabled={true}
                className="w-full h-9 px-3 text-sm border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                title="Country cannot be changed after account creation. Contact support if you need to change this."
              >
                <option value="USA">🇺🇸 USA (USD)</option>
                <option value="UAE">🇦🇪 UAE (AED)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                🔒 Country is locked after signup. Contact support to request a change.
              </p>
            </div>
          </div>
        </div>

        {/* Owner Information - Compact */}
        <div className="settings-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <User className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Owner Information</h3>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="field-label">Full Name <span className="text-red-500">*</span></Label>
              <Input
                value={orgData.ownerName}
                onChange={(e) => setOrgData({...orgData, ownerName: e.target.value})}
                placeholder="John Doe"
                className="h-9 text-sm"
              />
            </div>
            
            <div>
              <Label className="field-label">Email <span className="text-red-500">*</span></Label>
              <Input
                type="email"
                value={orgData.ownerEmail}
                disabled
                className="h-9 text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={() => setShowChangeEmailModal(true)}
                className="text-xs text-blue-600 hover:text-blue-700 hover:underline mt-1"
                data-testid="change-email-link"
              >
                Change email address
              </button>
              {pendingEmailChange && (
                <div className="flex items-center gap-1 text-xs text-amber-600 mt-1">
                  <Clock className="w-3 h-3" />
                  <span>Pending: {pendingEmailChange.email}</span>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await apiV2.post('/auth/account/cancel-email-change');
                        setPendingEmailChange(null);
                        toast.success('Email change cancelled');
                      } catch (e) {
                        toast.error('Failed to cancel');
                      }
                    }}
                    className="text-red-500 hover:text-red-700 hover:underline ml-1"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            
            <div>
              <Label className="field-label">Phone</Label>
              <Input
                value={orgData.ownerPhone || ''}
                onChange={(e) => setOrgData({...orgData, ownerPhone: e.target.value})}
                placeholder="+1-555-0100"
                className="h-9 text-sm"
              />
            </div>
            
            <div>
              <Label className="field-label">Password</Label>
              <Input
                type="password"
                value="••••••••"
                disabled
                className="h-9 text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(true)}
                className="text-xs text-blue-600 hover:text-blue-700 hover:underline mt-1"
                data-testid="change-password-link"
              >
                Change password
              </button>
            </div>
          </div>
        </div>
        </div>
        
        {/* Change Password Modal */}
        {showChangePasswordModal && (
          <ChangePasswordModal 
            onClose={() => setShowChangePasswordModal(false)} 
          />
        )}
        
        {/* Change Email Modal */}
        {showChangeEmailModal && (
          <ChangeEmailModal 
            currentEmail={orgData.ownerEmail}
            onClose={() => setShowChangeEmailModal(false)}
            onSuccess={() => {
              setShowChangeEmailModal(false);
              checkPendingEmailChange();
            }}
          />
        )}
      </>
    );
  }


  // BRANDING TAB
  if (activeSubView === 'settings-branding') {
    return (
      <div className="space-y-4">
        {/* Header with Save Button */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-gray-900">Branding & Appearance</h2>
          <Button
            onClick={handleSaveBranding}
            disabled={saving}
            size="sm"
            style={{ backgroundColor: '#2563EB' }}
            className="hover:opacity-90 h-8 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3 h-3 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>

        {/* Logo Upload */}
        <div className="settings-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Company Logo</h3>
          </div>
          
          <div className="flex items-start gap-4">
            {/* Logo Preview */}
            <div className="flex-shrink-0">
              {logoPreview ? (
                <div className="w-24 h-24 border-2 border-gray-300 rounded-lg overflow-hidden bg-white flex items-center justify-center">
                  <img 
                    src={logoPreview.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${logoPreview}` : logoPreview} 
                    alt="Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg bg-gray-100 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-gray-400" />
                </div>
              )}
            </div>
            
            {/* Upload Button */}
            <div className="flex-1">
              <label className="block">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  className="w-full h-9"
                  onClick={(e) => e.currentTarget.previousElementSibling.click()}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload Logo'
                  )}
                </Button>
              </label>
              <p className="text-xs text-gray-500 mt-2">
                PNG, JPG, or WebP. Max 2MB. Appears in navbar, emails, and booking widget.
              </p>
            </div>
          </div>
        </div>

        {/* Primary Color */}
        <div className="settings-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Palette className="w-4 h-4 text-gray-600" />
            <h3 className="font-semibold text-sm">Brand Color</h3>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={selectedColor}
                onChange={(e) => setSelectedColor(e.target.value)}
                className="w-12 h-12 rounded border-2 border-gray-300 cursor-pointer"
              />
              <div className="flex-1">
                <Label className="field-label">Primary Color</Label>
                <Input
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  placeholder="#2563EB"
                  className="h-9 text-sm font-mono"
                />
              </div>
            </div>
            
            {/* Color Preview */}
            <div className="p-3 bg-white rounded border">
              <p className="text-xs text-gray-600 mb-2">Preview</p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  className="h-8 text-xs"
                  style={{ backgroundColor: selectedColor, borderColor: selectedColor }}
                >
                  Primary Button
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 text-xs"
                  style={{ color: selectedColor, borderColor: selectedColor }}
                >
                  Outline Button
                </Button>
                <Badge style={{ backgroundColor: selectedColor }} className="text-xs">Badge</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Usage Info */}
        <div className="p-3 bg-blue-50 rounded border border-blue-200">
          <p className="text-xs text-blue-900 font-medium">📍 Where your branding appears:</p>
          <ul className="text-xs text-blue-700 mt-2 space-y-1 ml-4 list-disc">
            <li>Dashboard navbar & sidebar</li>
            <li>Customer booking widget</li>
            <li>Booking confirmation emails</li>
            <li>Customer trip tickets</li>
            <li>Waiver signing pages</li>
          </ul>
        </div>
      </div>
    );
  }

  // PREFERENCES TAB
  if (activeSubView === 'settings-preferences') {
    return (
      <div className="space-y-4">
        {/* Header with Save Button */}
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-base font-semibold text-gray-900">Display Preferences</h2>
          <Button
            onClick={handleSavePreferences}
            size="sm"
            style={{ backgroundColor: '#2563EB' }}
            className="hover:opacity-90 h-8 text-white"
          >
            <Save className="w-3 h-3 mr-1" />
            Save
          </Button>
        </div>

        {/* Date Format - Compact */}
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4" />
            Date Format
          </Label>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => setTempDateFormat('MM/DD/YYYY')}
              className={`p-2 border rounded-lg transition-colors text-center ${
                tempDateFormat === 'MM/DD/YYYY'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-xs">MM/DD/YYYY</div>
              <div className="text-xs text-gray-600 mt-0.5">10/17/2025</div>
            </button>
            <button
              onClick={() => setTempDateFormat('DD/MM/YYYY')}
              className={`p-2 border rounded-lg transition-colors text-center ${
                tempDateFormat === 'DD/MM/YYYY'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-xs">DD/MM/YYYY</div>
              <div className="text-xs text-gray-600 mt-0.5">17/10/2025</div>
            </button>
            <button
              onClick={() => setTempDateFormat('YYYY-MM-DD')}
              className={`p-2 border rounded-lg transition-colors text-center ${
                tempDateFormat === 'YYYY-MM-DD'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-xs">YYYY-MM-DD</div>
              <div className="text-xs text-gray-600 mt-0.5">2025-10-17</div>
            </button>
          </div>
        </div>

        {/* Time Format - Compact */}
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4" />
            Time Format
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setTempTimeFormat('12h')}
              className={`p-2 border rounded-lg transition-colors text-center ${
                tempTimeFormat === '12h'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-xs">12-hour</div>
              <div className="text-xs text-gray-600 mt-0.5">11:00 PM</div>
            </button>
            <button
              onClick={() => setTempTimeFormat('24h')}
              className={`p-2 border rounded-lg transition-colors text-center ${
                tempTimeFormat === '24h'
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="font-medium text-xs">24-hour</div>
              <div className="text-xs text-gray-600 mt-0.5">23:00</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // WAIVERS TAB
  if (activeSubView === 'settings-waivers') {
    return (
      <div className="space-y-4">
        {/* Header with Toggle and Save Button */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-gray-900">Waiver System</h2>
            {/* Enable/Disable Toggle */}
            <button
              onClick={() => handleToggleWaiver(!waiverEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                waiverEnabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
              title={waiverEnabled ? 'Click to disable waivers' : 'Click to enable waivers'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  waiverEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-600">
              {waiverEnabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>
          <Button
            onClick={handleSaveWaivers}
            disabled={saving || !waiverEnabled || !waiverText.trim() || waiverText.trim().length < 100}
            size="sm"
            style={{ backgroundColor: '#2563EB' }}
            className="hover:opacity-90 h-8 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-3 h-3 mr-1" />
                Save
              </>
            )}
          </Button>
        </div>

        {/* Status Notice */}
        {waiverEnabled ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900">Waivers Active</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  All customers must sign before viewing trip details. This protects your charter operation.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-gray-700">Waivers Disabled</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Enable waivers to require customer signatures before trip access.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Only show editor if enabled */}
        {waiverEnabled && (
          <>
            {/* Waiver Text Editor */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-gray-900">Your Waiver Text (Required)</Label>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowWaiverPreview(true)}
                    className="h-7 text-xs"
                    disabled={!waiverText.trim()}
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    Preview
                  </Button>
                  {waiverText.trim().length >= 100 && (
                    <Button
                      onClick={() => {
                        // Open print-friendly view
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(`
                          <!DOCTYPE html>
                          <html>
                          <head>
                            <title>Print Waiver - ${orgData.name}</title>
                            <style>
                              @media print { @page { margin: 0.5in; } }
                              body { font-family: Arial, sans-serif; max-width: 8.5in; margin: 0 auto; padding: 20px; line-height: 1.6; }
                              h1 { font-size: 24px; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
                              .waiver-text { white-space: pre-wrap; font-size: 12px; margin-bottom: 40px; }
                              .signature-section { margin-top: 60px; border-top: 2px solid #333; padding-top: 20px; }
                              .signature-line { border-bottom: 1px solid #333; margin: 30px 0; min-height: 40px; }
                              .label { font-size: 11px; color: #666; margin-bottom: 5px; }
                              @media print { .no-print { display: none; } }
                            </style>
                          </head>
                          <body>
                            <h1>${orgData.name || 'Waiver'}</h1>
                            <div class="waiver-text">${waiverText}</div>
                            <div class="signature-section">
                              <div class="label">Customer Name (Print)</div><div class="signature-line"></div>
                              <div class="label">Customer Signature</div><div class="signature-line"></div>
                              <div class="label">Date</div><div class="signature-line"></div>
                            </div>
                            <div class="no-print" style="margin-top: 40px; text-align: center;">
                              <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">Print Waiver</button>
                            </div>
                          </body>
                          </html>
                        `);
                        printWindow.document.close();
                      }}
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      Print
                    </Button>
                  )}
                </div>
              </div>
              <Textarea
                value={waiverText}
                onChange={(e) => setWaiverText(e.target.value)}
                placeholder="Enter your custom waiver text here. You must consult with a qualified attorney to create proper legal language for your jurisdiction."
                className="min-h-[200px] text-xs font-mono bg-white"
              />
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-500">
                  {waiverText.length} characters 
                  {waiverText.length < 100 && waiverText.length > 0 && (
                    <span className="text-red-600 ml-1">(minimum 100 required)</span>
                  )}
                </p>
                {waiverText.length >= 100 && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Valid length
                  </span>
                )}
              </div>
            </div>

            {/* Combined Important Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Important Legal Notice</p>
                    <p className="text-xs text-amber-800 mt-1">
                      BookingFlows provides NO legal templates and is NOT liable for your waiver content or enforceability. 
                      You MUST consult with a qualified attorney to create proper waiver language for your jurisdiction and business. 
                      Always keep printed waivers and pens on your asset as backup in case the digital system fails (no signal, device issues, etc.).
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* WAIVER LOOKUP - Search by email, name, or phone */}
            <div className="border border-blue-200 rounded-lg bg-blue-50/50">
              <button
                onClick={() => setShowWaiverSearch(!showWaiverSearch)}
                className="w-full flex items-center justify-between p-3 hover:bg-blue-50 transition-colors rounded-t-lg"
              >
                <div className="flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-semibold text-blue-900">Waiver Lookup</span>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">Search by email, name, or phone</span>
                </div>
                {showWaiverSearch ? <ChevronUp className="w-4 h-4 text-blue-600" /> : <ChevronDown className="w-4 h-4 text-blue-600" />}
              </button>
              
              {showWaiverSearch && (
                <div className="border-t border-blue-200 p-3 space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search waivers by email, name, or phone number..."
                      value={waiverSearchQuery}
                      onChange={(e) => setWaiverSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchingWaivers && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                  </div>
                  
                  {/* Search Results */}
                  {waiverSearchQuery.length >= 2 && (
                    <div className="bg-white rounded-lg border border-gray-200 max-h-96 overflow-y-auto">
                      {waiverSearchResults.length === 0 && !searchingWaivers ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No waivers found matching "{waiverSearchQuery}"
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100">
                          {waiverSearchResults.map((waiver, idx) => (
                            <div key={idx} className="p-3 hover:bg-gray-50">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-gray-900">{waiver.signatureName}</span>
                                    <Badge variant="outline" className="text-xs">
                                      {waiver.verificationMethod === 'sms' ? '✓ SMS' : waiver.verificationMethod}
                                    </Badge>
                                  </div>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
                                    <p><span className="text-gray-400">Email:</span> {waiver.email || 'N/A'}</p>
                                    <p><span className="text-gray-400">Phone:</span> {waiver.phone || 'N/A'}</p>
                                    <p><span className="text-gray-400">Service:</span> {waiver.serviceName}</p>
                                    <p><span className="text-gray-400">Trip:</span> {waiver.bookingDate ? new Date(waiver.bookingDate).toLocaleDateString() : 'N/A'}</p>
                                    <p><span className="text-gray-400">Signed:</span> {waiver.signedAt ? new Date(waiver.signedAt).toLocaleString() : 'N/A'}</p>
                                    <p><span className="text-gray-400">IP:</span> {waiver.ipAddress}</p>
                                  </div>
                                  {/* Device Fingerprint Details */}
                                  {waiver.deviceInfo && Object.keys(waiver.deviceInfo).length > 0 && (
                                    <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                                      <p className="font-medium text-gray-700 mb-1">Device Fingerprint:</p>
                                      <div className="grid grid-cols-2 gap-x-4 text-gray-600">
                                        <p><span className="text-gray-400">Platform:</span> {waiver.deviceInfo.platform || 'N/A'}</p>
                                        <p><span className="text-gray-400">Browser:</span> {waiver.deviceInfo.browser || 'N/A'}</p>
                                        <p><span className="text-gray-400">Screen:</span> {waiver.deviceInfo.screenResolution || waiver.deviceInfo.screenSize || 'N/A'}</p>
                                        <p><span className="text-gray-400">Language:</span> {waiver.deviceInfo.language || 'N/A'}</p>
                                      </div>
                                      {waiver.deviceInfo.userAgent && (
                                        <p className="mt-1 text-[10px] text-gray-500 truncate">
                                          <span className="text-gray-400">UA:</span> {waiver.deviceInfo.userAgent}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </div>
                                <span className="font-mono text-xs text-blue-600 flex-shrink-0 ml-2">{waiver.confirmationCode}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {waiverSearchQuery.length > 0 && waiverSearchQuery.length < 2 && (
                    <p className="text-xs text-gray-500 text-center">Type at least 2 characters to search</p>
                  )}
                </div>
              )}
            </div>

            {/* Signed Waivers - Collapsible */}
            {signedWaivers.length > 0 && (
              <div className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => setExpandedWaivers(!expandedWaivers)}
                  className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-semibold text-gray-900">Recent Signed Waivers ({signedWaivers.length})</span>
                  </div>
                  {expandedWaivers ? <ChevronUp className="w-4 h-4 text-gray-600" /> : <ChevronDown className="w-4 h-4 text-gray-600" />}
                </button>
                
                {expandedWaivers && (
                  <div className="border-t border-gray-200 bg-gray-50">
                    {/* Waiver List */}
                    <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
                      {signedWaivers
                        .slice((waiverPage - 1) * waiversPerPage, waiverPage * waiversPerPage)
                        .map((waiver, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono font-semibold text-blue-600 text-sm">{waiver.confirmationCode}</span>
                              <Badge variant={waiver.method === 'sms' ? 'default' : 'secondary'} className="text-xs">
                                {waiver.method === 'sms' ? '✓ SMS' : 'ID'}
                              </Badge>
                            </div>
                            <p className="text-xs text-gray-600">{waiver.customerName || 'Unknown'}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(waiver.signedAt).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            onClick={() => {
                              setSelectedWaiverDetails(waiver.trip);
                              setShowWaiverDetailModal(true);
                            }}
                            variant="outline"
                            size="sm"
                            className="flex-shrink-0"
                          >
                            View Details
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Pagination */}
                    {signedWaivers.length > waiversPerPage && (
                      <div className="border-t border-gray-200 p-3 flex items-center justify-between bg-white">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setWaiverPage(p => Math.max(1, p - 1))}
                          disabled={waiverPage === 1}
                          className="h-8 text-xs"
                        >
                          Previous
                        </Button>
                        <span className="text-xs text-gray-600">
                          Page {waiverPage} of {Math.ceil(signedWaivers.length / waiversPerPage)} ({signedWaivers.length} total)
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setWaiverPage(p => Math.min(Math.ceil(signedWaivers.length / waiversPerPage), p + 1))}
                          disabled={waiverPage >= Math.ceil(signedWaivers.length / waiversPerPage)}
                          className="h-8 text-xs"
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {/* Detailed Waiver Modal (Logging-only, no PIN) */}
        <Dialog open={showWaiverDetailModal} onOpenChange={(open) => {
          if (!open) {
            setSelectedWaiverDetails(null);
          } else if (selectedWaiverDetails) {
            // Log viewing when modal opens
            handleVerifyWaiverPin();
          }
          setShowWaiverDetailModal(open);
        }}>
          <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>Waiver Signatures ({selectedWaiverDetails?.waiverSignatures?.length || 1})</DialogTitle>
              <DialogDescription>
                Complete signature data - viewing is logged for privacy compliance
              </DialogDescription>
            </DialogHeader>
            
            {/* Privacy Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex-shrink-0">
              <p className="text-xs text-blue-800">
                <strong>Privacy Notice:</strong> You're viewing protected customer data. This access is automatically logged in the activity log for data privacy compliance. Only view this information when necessary for legal defense.
              </p>
            </div>
            
            {/* Scrollable Signatures */}
            <div className="flex-1 overflow-y-auto min-h-0 pr-2">
              {selectedWaiverDetails && (
                <div className="space-y-4 py-2">
                
                {(selectedWaiverDetails.waiverSignatures || [selectedWaiverDetails.waiverRecord]).filter(Boolean).map((sig, idx) => {
                  return (
                  <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-900">Signature #{idx + 1}</h3>
                      <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        sig.verificationMethod === 'sms' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                      }`}>
                        {sig.verificationMethod === 'sms' ? '✓ SMS Verified' : 'ID Required'}
                      </div>
                    </div>
                    
                    {/* All Info (No PIN needed - logging only) */}
                    <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Name</label>
                        <p className="text-gray-900">{sig.signatureName || 'Unknown'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Phone</label>
                        <p className="text-gray-900 font-mono text-xs">{sig.phoneNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">PIN</label>
                        <p className="font-mono font-bold text-green-600">{sig.pin || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Signed At</label>
                        <p className="text-gray-900 text-xs">{sig.signedAt ? new Date(sig.signedAt).toLocaleString() : 'Unknown'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">IP Address</label>
                        <p className="font-mono text-xs text-gray-900">{sig.ipAddress || 'Unknown'}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">Device</label>
                        <p className="text-xs text-gray-900">{sig.deviceInfo?.platform || 'Unknown'}</p>
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-semibold text-gray-600 mb-0.5">User Agent</label>
                        <p className="text-xs text-gray-700 break-all">{sig.deviceInfo?.userAgent?.substring(0, 100) || 'N/A'}...</p>
                      </div>
                    </div>
                    
                    {/* Device Fingerprint (Collapsible) */}
                    {sig.deviceInfo && Object.keys(sig.deviceInfo).length > 0 && (
                      <details className="mt-3">
                        <summary className="cursor-pointer text-xs font-semibold text-blue-600 hover:text-blue-800">
                          View Complete Device Fingerprint
                        </summary>
                        <div className="mt-2 p-3 bg-white rounded border border-gray-200 max-h-64 overflow-y-auto">
                          <pre className="text-xs text-gray-700 whitespace-pre-wrap">
                            {JSON.stringify(sig.deviceInfo, null, 2)}
                          </pre>
                        </div>
                      </details>
                    )}
                    
                    {/* Signature & ID Images */}
                    {sig.signatureImage && (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Signature</label>
                        <div className="bg-white border border-gray-300 rounded p-2">
                          <img src={sig.signatureImage} alt="Signature" className="w-full h-20 object-contain" />
                        </div>
                      </div>
                    )}
                    
                    {sig.idImage && (
                      <div className="mt-3">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Government ID</label>
                        <div className="bg-white border border-gray-300 rounded p-2">
                          <img src={sig.idImage} alt="ID" className="w-full max-h-32 object-contain" />
                        </div>
                      </div>
                    )}
                  </div>
                );
                })}
              </div>
            )}
            </div>
            
            {/* Footer with Close Button */}
            <div className="flex-shrink-0 pt-4 border-t">
              <Button
                onClick={() => setShowWaiverDetailModal(false)}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Waiver Preview Modal */}
        <Dialog open={showWaiverPreview} onOpenChange={setShowWaiverPreview}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                Waiver Preview
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4 bg-white border border-gray-200 rounded-lg p-6">
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">{orgData.name || 'Waiver'}</h2>
                  <p className="text-sm text-gray-600 mt-1">Liability Waiver and Release</p>
                </div>
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 leading-relaxed">
{waiverText}
                  </pre>
                </div>
                <div className="mt-8 pt-6 border-t border-gray-300 space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Participant Name (Print)</label>
                    <div className="border-b-2 border-gray-400 pb-1"></div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Participant Signature</label>
                    <div className="border-b-2 border-gray-400 pb-1"></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <div className="border-b-2 border-gray-400 pb-1"></div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <div className="border-b-2 border-gray-400 pb-1"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button
                onClick={() => setShowWaiverPreview(false)}
                variant="outline"
                className="flex-1"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }



  // API KEYS TAB
  if (activeSubView === 'settings-api-keys') {
    return (
      <div className="space-y-4">
        <SettingsHeader
          title="API Keys"
          subtitle={`Programmatic access to your data. ${apiKeys.length}/5 keys in use.`}
        >
          <Button
            size="sm"
            onClick={() => setShowNewKeyModal(true)}
            disabled={apiKeys.length >= 5}
            style={{ backgroundColor: '#2563EB' }}
            className="h-8 hover:opacity-90 text-white"
          >
            <Plus className="w-3 h-3 mr-1" />
            Generate Key
          </Button>
        </SettingsHeader>
        
        {/* Max Keys Warning */}
        {apiKeys.length >= 5 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
            <p className="text-xs text-yellow-900">
              Maximum of 5 API keys reached. Revoke an existing key to generate a new one.
            </p>
          </div>
        )}


        {/* API Keys List */}
        <div className="settings-card overflow-hidden">
          {loadingKeys ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm">No API keys yet</p>
              <p className="text-xs text-gray-400 mt-1">Generate your first key to get started</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {apiKeys.map((key) => {
                const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
                const isExpanded = expandedKeyId === key.id;
                const stats = keyUsageStats[key.id];
                const isExpired = key.expiresAt && new Date(key.expiresAt) < new Date();
                const isInactive = !key.isActive || isExpired;
                
                return (
                  <div key={key.id} className={`p-4 ${isInactive ? 'opacity-40 bg-gray-50' : ''}`}>
                    {/* Key Header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm text-gray-900">{key.name}</span>
                          <div className="text-xs text-gray-500 text-right">
                            <div>Created {new Date(key.createdAt).toLocaleDateString()}</div>
                            {key.lastUsedAt ? (
                              <div className="text-gray-600">Last used {new Date(key.lastUsedAt).toLocaleString()}</div>
                            ) : (
                              <div className="text-gray-400">Never used</div>
                            )}
                            {key.expiresAt && (
                              <div className={isExpired ? 'text-red-600 font-medium' : 'text-orange-600'}>
                                {isExpired ? 'Expired' : 'Expires'} {new Date(key.expiresAt).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 ml-3">
                        {!isInactive && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleKeyExpanded(key.id)}
                            className="h-7 text-xs"
                            data-testid={`apikey-toggle-${key.id}`}
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRevokeKey(key.id, key.name)}
                          disabled={isInactive}
                          className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50"
                          data-testid={`apikey-revoke-${key.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Expanded — Inline stats + endpoint list with this key pre-filled */}
                    {!isInactive && isExpanded && (
                      <div className="mt-4 space-y-3" data-testid={`apikey-expanded-${key.id}`}>
                        {stats && (
                          <div className="grid grid-cols-3 gap-2">
                            <div className="settings-card-inner p-2 text-center">
                              <p className="text-sm font-semibold text-gray-900">{stats.totalRequestsToday || 0}</p>
                              <p className="text-xs text-gray-500">Today</p>
                            </div>
                            <div className="settings-card-inner p-2 text-center">
                              <p className="text-sm font-semibold text-gray-900">{stats.remainingRequests ?? 1000}</p>
                              <p className="text-xs text-gray-500">Remaining/hr</p>
                            </div>
                            <div className="settings-card-inner p-2 text-center">
                              <p className="text-sm font-semibold text-gray-900 truncate">{stats.lastIpAddress || '—'}</p>
                              <p className="text-xs text-gray-500">Last IP</p>
                            </div>
                          </div>
                        )}

                        <div>
                          <p className="text-xs font-semibold text-gray-700 mb-1.5">Available endpoints</p>
                          <div className="space-y-1.5">
                            {[
                              { name: 'Bookings', path: '/api/v2/public/api/bookings', desc: 'All bookings with dates, times, assignments' },
                              { name: 'Team', path: '/api/v2/public/api/team', desc: 'Team roster with roles and availability' },
                              { name: 'Assets', path: '/api/v2/public/api/assets', desc: 'Resources with capacity and status' },
                              { name: 'Services', path: '/api/v2/public/api/services', desc: 'Service catalog with pricing and availability' },
                              { name: 'Waivers', path: '/api/v2/public/api/waivers', desc: 'Signed waivers with verification data' },
                            ].map((endpoint) => {
                              // Use the actual key value if we have it from key generation; otherwise show YOUR_KEY
                              const displayKey = key.keyValue || key.value || 'YOUR_KEY';
                              const fullUrl = `${BACKEND_URL}${endpoint.path}?api_key=${displayKey}`;
                              return (
                                <div key={endpoint.path} className="settings-card-inner p-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-sm font-semibold text-gray-900">{endpoint.name}</span>
                                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-gray-200 text-gray-500">GET</span>
                                      </div>
                                      <p className="text-xs text-gray-500 mb-2">{endpoint.desc}</p>
                                      <code className="block text-[11px] text-blue-600 break-all font-mono">{fullUrl}</code>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => copyToClipboard(fullUrl)}
                                      className="h-7 px-2 flex-shrink-0"
                                      data-testid={`apikey-${key.id}-copy-${endpoint.name.toLowerCase()}`}
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {!key.keyValue && !key.value && (
                            <p className="text-[11px] text-gray-500 mt-2">
                              Replace <code className="bg-gray-100 px-1 rounded">YOUR_KEY</code> with the key value you saved when generating.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Rate Limits Info */}
        <div className="settings-card p-3">
          <p className="text-xs font-semibold text-gray-900 mb-1">Rate Limits</p>
          <p className="text-xs text-gray-700">
            Each API key is limited to <strong>1,000 requests per hour</strong>. Exceeding this limit returns 429.
          </p>
        </div>



        {/* Generated Key Success Modal */}
        <Dialog open={showGeneratedKeyModal} onOpenChange={(open) => {
          if (!open) {
            // Cleanup when closing
            if (pollInterval) {
              clearInterval(pollInterval);
              setPollInterval(null);
            }
            setShowGeneratedKeyModal(false);
            setGeneratedKey(null);
            setKeyIsLive(false);
            setCheckingKeyStatus(false);
            setExpandedEndpoint(null);
            setEndpointTestStatus({
              bookings: 'idle',
              team: 'idle',
              assets: 'idle',
              services: 'idle',
              waivers: 'idle'
            });
            setEndpointResponses({
              bookings: null,
              team: null,
              assets: null,
              services: null,
              waivers: null
            });
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Key className="w-5 h-5 text-blue-600" />
                API Key Generated!
              </DialogTitle>
            </DialogHeader>
            
            {generatedKey && (
              <div className="space-y-4 mt-4">
                {/* The API Key */}
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                  <Label className="text-xs font-medium text-gray-700">Your API Key</Label>
                  <div className="bg-white rounded-lg border border-gray-200 p-3 mt-2 mb-3">
                    <code className="block font-mono text-sm text-gray-900 break-all">
                      {generatedKey.key}
                    </code>
                  </div>
                  <Button
                    onClick={() => copyToClipboard(generatedKey.key)}
                    style={{ backgroundColor: '#2563EB' }}
                    className="w-full hover:opacity-90 text-white"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy API Key
                  </Button>
                  
                  {/* Warning Text - Under Button */}
                  <div className="mt-3 flex items-start gap-2 text-red-700">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <p className="text-xs">
                      This key won't be shown again. Copy it to a secure location now.
                    </p>
                  </div>
                </div>
                
                {/* Test All Endpoints - NO WRAPPER DIV */}
                <div className="space-y-3 pt-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-900">Test Your Endpoints</h3>
                    {/* Success Counter Badge */}
                    {Object.values(endpointTestStatus).filter(s => s === 'success').length > 0 && (
                      <Badge className="bg-green-600 text-white text-xs">
                        {Object.values(endpointTestStatus).filter(s => s === 'success').length}/6 Working
                      </Badge>
                    )}
                  </div>
                  
                  {/* Endpoint Test Cards */}
                  <div className="space-y-2">
                    {[
                      { id: 'bookings', name: 'Bookings', path: '/api/v2/public/api/bookings', desc: 'All bookings with dates, times, assignments', icon: Calendar },
                      { id: 'team', name: 'Team', path: '/api/v2/public/api/team', desc: 'Team roster with roles and availability', icon: Users },
                      { id: 'assets', name: 'Assets', path: '/api/v2/public/api/assets', desc: 'Resources with capacity and status', icon: Package },
                      { id: 'services', name: 'Services', path: '/api/v2/public/api/services', desc: 'Service catalog with pricing', icon: Sparkles },
                      { id: 'waivers', name: 'Waivers', path: '/api/v2/public/api/waivers', desc: 'Signed waivers with verification', icon: Shield }
                    ].map((endpoint) => {
                      const Icon = endpoint.icon;
                      return (
                        <div key={endpoint.id} className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                          {/* Endpoint Header - Always Visible */}
                          <div className="p-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1">
                              <Icon className="w-4 h-4 text-gray-600 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 truncate">{endpoint.name}</p>
                                <p className="text-xs text-gray-500 hidden sm:block truncate">{endpoint.desc}</p>
                              </div>
                            </div>
                            
                            {/* Test Button & Status */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {endpointTestStatus[endpoint.id] === 'success' && (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              )}
                              {endpointTestStatus[endpoint.id] === 'error' && (
                                <XCircle className="w-4 h-4 text-red-600" />
                              )}
                              <Button
                                size="sm"
                                onClick={() => {
                                  if (expandedEndpoint === endpoint.id) {
                                    setExpandedEndpoint(null);
                                  } else {
                                    setExpandedEndpoint(endpoint.id);
                                  }
                                }}
                                variant={expandedEndpoint === endpoint.id ? "default" : "outline"}
                                className={`h-8 px-3 text-xs ${
                                  expandedEndpoint === endpoint.id 
                                    ? 'bg-gray-900 hover:bg-gray-800 text-white' 
                                    : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
                                }`}
                              >
                                {endpointTestStatus[endpoint.id] === 'testing' ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  expandedEndpoint === endpoint.id ? 'Hide' : 'Test'
                                )}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Expanded Details */}
                          {expandedEndpoint === endpoint.id && (
                            <div className="px-3 pb-3 border-t border-gray-100 bg-gray-50 space-y-2.5 pt-3">
                              {/* Description on mobile */}
                              <p className="text-xs text-gray-600 sm:hidden">{endpoint.desc}</p>
                              
                              {/* Curl Command - Enhanced */}
                              <div className="bg-gray-950 rounded-lg p-3 border border-gray-800">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500 font-mono">$</span>
                                    <span className="text-xs text-gray-400">cURL Command</span>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => copyToClipboard(`curl -X GET "${BACKEND_URL}${endpoint.path}?api_key=${generatedKey.key}"`)}
                                    className="h-6 px-2 flex-shrink-0 hover:bg-gray-800 text-gray-400 hover:text-white"
                                  >
                                    <Copy className="w-3 h-3" />
                                  </Button>
                                </div>
                                <code className="block text-xs font-mono break-all overflow-x-auto">
                                  <span className="text-green-400">curl</span>
                                  <span className="text-yellow-400"> -X GET </span>
                                  <span className="text-blue-400">"{BACKEND_URL}{endpoint.path}?api_key=</span>
                                  <span className="text-purple-400">{generatedKey.key}</span>
                                  <span className="text-blue-400">"</span>
                                </code>
                              </div>
                              
                              {/* Run Test Button */}
                              <Button
                                onClick={() => testEndpoint(endpoint.id, endpoint.path)}
                                disabled={endpointTestStatus[endpoint.id] === 'testing'}
                                style={{ backgroundColor: '#2563EB' }}
                                className="w-full h-9 text-xs font-medium hover:opacity-90 text-white"
                              >
                                {endpointTestStatus[endpoint.id] === 'testing' ? (
                                  <>
                                    <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                    Testing...
                                  </>
                                ) : endpointTestStatus[endpoint.id] === 'success' ? (
                                  <>
                                    <CheckCircle className="w-3.5 h-3.5 mr-2" />
                                    Test Again
                                  </>
                                ) : endpointTestStatus[endpoint.id] === 'error' ? (
                                  <>
                                    <AlertCircle className="w-3.5 h-3.5 mr-2" />
                                    Retry Test
                                  </>
                                ) : (
                                  'Run Test'
                                )}
                              </Button>
                              
                              {/* Response Terminal View */}
                              {endpointResponses[endpoint.id] && (
                                <div className="bg-gray-950 rounded-lg overflow-hidden border border-gray-800">
                                  {/* Terminal Header */}
                                  <div className="flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-800">
                                    <div className="flex items-center gap-2">
                                      <div className="flex gap-1.5">
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                                        <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                                      </div>
                                      <span className="text-xs text-gray-400 font-mono">JSON Response</span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => copyToClipboard(JSON.stringify(endpointResponses[endpoint.id], null, 2))}
                                      className="h-6 px-2 text-gray-400 hover:text-white hover:bg-gray-800"
                                    >
                                      <Copy className="w-3 h-3" />
                                    </Button>
                                  </div>
                                  {/* Terminal Content - Enhanced JSON */}
                                  <div className="p-3 max-h-64 overflow-auto">
                                    <pre className="text-xs font-mono whitespace-pre-wrap">
                                      <code dangerouslySetInnerHTML={{
                                        __html: JSON.stringify(endpointResponses[endpoint.id], null, 2)
                                          .replace(/"([^"]+)":/g, '<span class="text-cyan-400">"$1"</span>:')
                                          .replace(/: "([^"]*)"/g, ': <span class="text-green-400">"$1"</span>')
                                          .replace(/: (\d+)/g, ': <span class="text-yellow-400">$1</span>')
                                          .replace(/: (true|false)/g, ': <span class="text-orange-400">$1</span>')
                                          .replace(/: null/g, ': <span class="text-gray-500">null</span>')
                                      }} />
                                    </pre>
                                  </div>
                                </div>
                              )}
                              
                              {/* Status Message - Only show if no response yet */}
                              {!endpointResponses[endpoint.id] && (
                                <>
                                  {endpointTestStatus[endpoint.id] === 'success' && (
                                    <div className="flex items-center gap-2 p-2.5 bg-green-50 rounded-lg border border-green-200">
                                      <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                      <p className="text-xs text-green-800 font-medium">Endpoint working perfectly!</p>
                                    </div>
                                  )}
                                  {endpointTestStatus[endpoint.id] === 'error' && (
                                    <div className="flex items-center gap-2 p-2.5 bg-red-50 rounded-lg border border-red-200">
                                      <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                                      <p className="text-xs text-red-800 font-medium">Test failed. Check your API key.</p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Close Button */}
                <Button
                  onClick={() => {
                    // Cleanup interval
                    if (pollInterval) {
                      clearInterval(pollInterval);
                      setPollInterval(null);
                    }
                    setShowGeneratedKeyModal(false);
                    setGeneratedKey(null);
                    setKeyIsLive(false);
                    setCheckingKeyStatus(false);
                    setExpandedEndpoint(null);
                    setEndpointTestStatus({
                      bookings: 'idle',
                      team: 'idle',
                      assets: 'idle'
                    });
                    setEndpointResponses({
                      bookings: null,
                      team: null,
                      assets: null
                    });
                  }}
                  className="w-full"
                  variant="outline"
                >
                  Close
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Generate Key Modal */}
        <Dialog open={showNewKeyModal} onOpenChange={setShowNewKeyModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Generate New API Key</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Key Name <span className="text-red-500">*</span></Label>
                <Input
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., Production Server"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Choose a descriptive name to identify this key
                </p>
              </div>
              
              <div>
                <Label className="text-sm">Expiration</Label>
                <select
                  value={newKeyExpiration}
                  onChange={(e) => setNewKeyExpiration(e.target.value)}
                  className="w-full mt-1 h-9 px-3 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                  <option value="365">1 year</option>
                  <option value="never">Never expires</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Key will automatically be revoked after this period
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleGenerateKey}
                  disabled={saving || !newKeyName.trim()}
                  style={{ backgroundColor: '#2563EB' }}
                  className="flex-1 hover:opacity-90 text-white"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Key className="w-4 h-4 mr-2" />
                      Generate Key
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNewKeyModal(false);
                    setNewKeyName('');
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }


  // ACTIVITY LOG TAB
  if (activeSubView === 'settings-activity') {
    return (
      <div className="space-y-4">
        <SettingsHeader
          title={isTeamMember ? 'My Activity' : 'Activity Log'}
          subtitle={isTeamMember
            ? 'A timeline of actions you have taken in this organization.'
            : 'Every CRUD action across the org, scoped by user and entity.'}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowActivityFilters(!showActivityFilters)}
            className="h-8"
          >
            <Filter className="w-3 h-3 mr-1" />
            Filters
          </Button>
          <Button
            size="sm"
            onClick={handleExportActivities}
            style={{ backgroundColor: '#2563EB' }}
            className="h-8 hover:opacity-90 text-white"
          >
            <Download className="w-3 h-3 mr-1" />
            Export
          </Button>
        </SettingsHeader>

        {/* Filters */}
        {showActivityFilters && (
          <div className="settings-card p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="field-label">Action</Label>
                <select
                  value={activityFilters.action}
                  onChange={(e) => setActivityFilters({...activityFilters, action: e.target.value})}
                  className="w-full h-8 px-2 text-xs border rounded"
                >
                  <option value="">All Actions</option>
                  <option value="CREATE">Create</option>
                  <option value="UPDATE">Update</option>
                  <option value="DELETE">Delete</option>
                  <option value="LOGIN">Login</option>
                  <option value="REFUND">Refund</option>
                  <option value="CANCEL">Cancel</option>
                  <option value="RESCHEDULE">Reschedule</option>
                  <option value="ASSIGN">Assign</option>
                </select>
              </div>
              <div>
                <Label className="field-label">Resource Type</Label>
                <select
                  value={activityFilters.resourceType}
                  onChange={(e) => setActivityFilters({...activityFilters, resourceType: e.target.value})}
                  className="w-full h-8 px-2 text-xs border rounded"
                >
                  <option value="">All Types</option>
                  <option value="Booking">Booking</option>
                  <option value="Service">Service</option>
                  <option value="Asset">Asset</option>
                  <option value="TeamMember">Team Member</option>
                  <option value="Role">Role</option>
                  <option value="Flow">Flow</option>
                  <option value="Waiver">Waiver</option>
                  <option value="Branding">Branding</option>
                  <option value="OwnerProfile">Owner Profile</option>
                  <option value="Notifications">Notifications</option>
                  <option value="Organization">Organization</option>
                  <option value="Integration">Integration</option>
                  <option value="User">User</option>
                </select>
              </div>
              <div>
                <Label className="field-label">Start Date</Label>
                <Input
                  type="date"
                  value={activityFilters.startDate}
                  onChange={(e) => setActivityFilters({...activityFilters, startDate: e.target.value})}
                  className="h-8 text-xs"
                />
              </div>
              <div>
                <Label className="field-label">End Date</Label>
                <Input
                  type="date"
                  value={activityFilters.endDate}
                  onChange={(e) => setActivityFilters({...activityFilters, endDate: e.target.value})}
                  className="h-8 text-xs"
                />
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => { setActivityPage(1); fetchActivities(); }}
              style={{ backgroundColor: '#2563EB' }}
              className="w-full h-8 text-xs hover:opacity-90 text-white"
            >
              Apply Filters
            </Button>
          </div>
        )}

        {/* Activity List */}
        <div className="settings-card overflow-hidden">
          {loadingActivities ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Activity className="w-12 h-12 text-gray-400 mx-auto mb-2" />
              <p className="text-sm">No activity recorded yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
              {activities.map((activity, idx) => {
                const action = (activity.action || '').toUpperCase();
                const actionStyle = (() => {
                  if (action === 'CREATE' || action === 'CREATED') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
                  if (action === 'DELETE' || action === 'DELETED' || action === 'CANCEL' || action === 'CANCELLED') return 'border-red-200 bg-red-50 text-red-700';
                  if (action === 'REFUND') return 'border-amber-200 bg-amber-50 text-amber-700';
                  if (action === 'LOGIN') return 'border-blue-200 bg-blue-50 text-blue-700';
                  if (action === 'UPDATE' || action === 'UPDATED') return 'border-gray-200 bg-gray-50 text-gray-700';
                  return 'border-gray-200 bg-gray-50 text-gray-600';
                })();

                const userType = (activity.userType || '').toLowerCase();
                const userTypeLabel = userType === 'admin' ? 'Admin' : userType === 'teammember' ? 'Team' : userType === 'system' ? 'System' : userType || '';

                return (
                  <div key={idx} className="p-3 hover:bg-gray-50/60" data-testid={`activity-row-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-md border ${actionStyle}`}>
                            {action}
                          </span>
                          <span className="text-gray-700 font-medium">{activity.entityType || 'Unknown'}</span>
                        </div>
                        {activity.description && (
                          <p className="text-sm text-gray-700 mt-1">{activity.description}</p>
                        )}
                        {activity.changes && typeof activity.changes === 'object' && Object.keys(activity.changes).length > 0 && (
                          <p className="text-xs text-gray-500 mt-1 truncate font-mono">
                            {Object.entries(activity.changes).slice(0, 3).map(([k, v]) => {
                              if (v && typeof v === 'object' && 'from' in v && 'to' in v) {
                                return `${k}: ${JSON.stringify(v.from)} → ${JSON.stringify(v.to)}`;
                              }
                              return `${k}: ${JSON.stringify(v)}`;
                            }).join(' · ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="flex items-center justify-end gap-1.5 text-xs">
                          {userTypeLabel && (
                            <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded ${
                              userType === 'admin'
                                ? 'bg-blue-100 text-blue-700'
                                : userType === 'teammember'
                                ? 'bg-violet-100 text-violet-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>{userTypeLabel}</span>
                          )}
                          <span className="text-gray-700 truncate max-w-[160px]">{activity.userName || 'System'}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {activity.createdAt ? new Date(activity.createdAt).toLocaleString() : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {activityTotalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActivityPage(p => Math.max(1, p - 1))}
              disabled={activityPage === 1}
              className="h-8 text-xs"
            >
              Previous
            </Button>
            <span className="text-xs text-gray-600">
              Page {activityPage} of {activityTotalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setActivityPage(p => Math.min(activityTotalPages, p + 1))}
              disabled={activityPage === activityTotalPages}
              className="h-8 text-xs"
            >
              Next
            </Button>
          </div>
        )}

        {/* Export Date-Range Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4" onClick={() => !exporting && setShowExportModal(false)} data-testid="activity-export-modal">
            <div className="bg-white rounded-xl border border-gray-200 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Export activity log</h3>
              <p className="text-sm text-gray-500 mb-5">
                CSV download of every log entry in the date range you choose.
                {isTeamMember ? ' Scoped to your own actions.' : ''}
              </p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div>
                  <Label className="text-xs text-gray-500">Start date</Label>
                  <Input
                    type="date"
                    value={exportRange.start}
                    onChange={(e) => setExportRange((r) => ({ ...r, start: e.target.value }))}
                    className="h-9 mt-1"
                    disabled={exporting}
                    data-testid="export-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">End date</Label>
                  <Input
                    type="date"
                    value={exportRange.end}
                    onChange={(e) => setExportRange((r) => ({ ...r, end: e.target.value }))}
                    className="h-9 mt-1"
                    disabled={exporting}
                    data-testid="export-end-date"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowExportModal(false)}
                  disabled={exporting}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmExport}
                  disabled={exporting}
                  style={{ backgroundColor: '#2563EB' }}
                  className="flex-1 hover:opacity-90 text-white"
                  data-testid="export-confirm-btn"
                >
                  {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting</> : <><Download className="w-4 h-4 mr-2" />Download CSV</>}
                </Button>
              </div>
              <p className="text-xs text-gray-400 mt-3">Max 50,000 rows per export.</p>
            </div>
          </div>
        )}

      </div>
    );
  }

  // PASSKEYS TAB — own dedicated tab, visible to admin and team members
  if (activeSubView === 'settings-passkeys') {
    // Wrap ALL logic in a proper component so React hooks rules are satisfied
    const PasskeysTabContent = () => {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const [passkeys, setPasskeys] = useState([]);
      const [registering, setRegistering] = useState(false);
      const [supported, setSupported] = useState(null);

      useEffect(() => {
        if (window.PublicKeyCredential &&
          typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
          window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
            .then(ok => setSupported(ok)).catch(() => setSupported(false));
        } else {
          setSupported(false);
        }
        apiV2.get('/auth/webauthn/credentials')
          .then(r => setPasskeys(r.data.credentials || []))
          .catch(() => {});
      }, []);

    const parseB64 = s => {
      if (!s) return new Uint8Array();
      const b = s.replace(/-/g,'+').replace(/_/g,'/');
      const padded = b.padEnd(b.length + (4 - b.length % 4) % 4, '=');
      try {
        const bin = atob(padded);
        return Uint8Array.from(bin, c => c.charCodeAt(0));
      } catch { return new Uint8Array(); }
    };
    const toB64url = buf => {
      const bytes = new Uint8Array(buf);
      let str = '';
      bytes.forEach(b => str += String.fromCharCode(b));
      return btoa(str).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,'');
    };

    const handleRegister = async () => {
      setRegistering(true);
      try {
        const token = localStorage.getItem('jwtToken');
        // Use axios (XHR) not fetch() — service worker intercepts fetch and consumes
        // the response body before our code can read it, causing 'body already used'
        const _axios = (await import('axios')).default;
        const axInst = _axios.create({
          baseURL: API_URL,
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
        });

        // Step 1: Get registration options
        const optRes = await axInst.post('/api/v2/auth/webauthn/register-options', {
          origin: window.location.origin
        });
        const options = optRes.data;
        if (!options.challenge) throw new Error('Invalid server response — missing challenge');

        // Step 2: Create credential (triggers Face ID / Touch ID)
        const pko = {
          challenge: parseB64(options.challenge),
          rp: { id: options.rp?.id, name: options.rp?.name },
          user: {
            id: parseB64(options.user?.id),
            name: options.user?.name,
            displayName: options.user?.displayName,
          },
          pubKeyCredParams: (options.pubKeyCredParams || []).map(p => ({
            type: p.type, alg: p.alg
          })),
          timeout: options.timeout || 60000,
          excludeCredentials: (options.excludeCredentials || []).map(c => ({
            type: 'public-key', id: parseB64(c.id)
          })),
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            residentKey: options.authenticatorSelection?.residentKey || 'preferred',
            userVerification: options.authenticatorSelection?.userVerification || 'preferred',
          },
          attestation: 'none',
        };
        const cred = await navigator.credentials.create({ publicKey: pko });

        // Step 3: Send credential to server — read body ONCE
        const deviceName = (() => {
          const ua = navigator.userAgent;
          if (/iPhone/.test(ua)) return 'iPhone';
          if (/iPad/.test(ua)) return 'iPad';
          if (/Mac/.test(ua)) return 'Mac';
          if (/Android/.test(ua)) return 'Android';
          if (/Windows/.test(ua)) return 'Windows PC';
          return 'This device';
        })();
        const body = {
          id: cred.id,
          rawId: toB64url(cred.rawId),
          type: cred.type,
          response: {
            attestationObject: toB64url(cred.response.attestationObject),
            clientDataJSON: toB64url(cred.response.clientDataJSON),
          },
          deviceName,
        };
        const regRes = await axInst.post('/api/v2/auth/webauthn/register', {
          ...body,
          origin: window.location.origin,  // needed so server verifies against public URL not internal cluster URL
        });
        toast.success(`Passkey set up on ${deviceName}! You can now sign in with Face ID / Touch ID.`);
        const refresh = await apiV2.get('/auth/webauthn/credentials');
        setPasskeys(refresh.data.credentials || []);
      } catch (err) {
        if (err.name === 'NotAllowedError') toast.error('Registration cancelled — try again');
        else if (err.name === 'NotSupportedError') toast.error('This device does not support passkeys');
        else { console.error('[Passkey register]', err); toast.error(err.message || 'Failed to set up passkey'); }
      } finally {
        setRegistering(false);
      }
    };

    const handleDelete = async (credId) => {
      try {
        await apiV2.delete(`/auth/webauthn/credentials/${credId}`);
        setPasskeys(p => p.filter(c => c.id !== credId));
        toast.success('Passkey removed');
      } catch { toast.error('Failed to remove passkey'); }
    };

    return (
      <div className="space-y-4 w-full">
        <SettingsHeader
          title="Face ID / Touch ID Login"
          subtitle="Sign in without a password using your device's biometrics. The private key never leaves your device."
        >
          {supported === true && (
            <Button
              onClick={handleRegister}
              disabled={registering}
              size="sm"
              style={{ backgroundColor: '#2563EB' }}
              className="h-8 hover:opacity-90 text-white"
              data-testid="passkey-add-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              {registering ? 'Setting up...' : 'Add Passkey'}
            </Button>
          )}
        </SettingsHeader>

        {supported === null && (
          <div className="settings-card p-4 text-sm text-gray-500">Checking support...</div>
        )}

        {supported === false && (
          <div className="settings-card p-4 text-sm text-gray-600">
            <p className="font-medium mb-1">Not supported on this browser</p>
            <p className="text-xs text-gray-500">
              Supported on Safari (iPhone/Mac with Face ID/Touch ID), Chrome (Android fingerprint), and Edge (Windows Hello).
            </p>
          </div>
        )}

        {supported === true && (
          <>
            {passkeys.length === 0 ? (
              <div className="settings-card p-8 text-center">
                <p className="text-sm text-gray-600 mb-1">No passkeys registered yet</p>
                <p className="text-xs text-gray-500">Add one to enable Face ID / Touch ID login</p>
              </div>
            ) : (
              <div className="settings-card divide-y divide-gray-200">
                {passkeys.map(pk => (
                  <div key={pk.id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{pk.deviceName || 'Device'}</p>
                        <p className="text-xs text-gray-500">
                          Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                          {pk.lastUsedAt && ` · Last used ${new Date(pk.lastUsedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => handleDelete(pk.id)} className="text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded hover:bg-red-50">
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
    }; // end PasskeysTabContent
    return <PasskeysTabContent />;
  }

  // CALENDAR TAB
  if (activeSubView === 'settings-calendar') {
    const isTM = isTeamMember; // set above in calendar functions section
    return (
      <div className="space-y-4">
        <SettingsHeader
          title="Calendar Sync"
          subtitle={isTM
            ? 'Subscribe to your assigned trips in Apple Calendar, Google, or Outlook.'
            : 'Subscribe to all bookings in Apple Calendar, Google, or Outlook.'}
        />

        <div className="settings-card p-5">
          <h3 className="text-sm font-semibold text-gray-900 mb-1">
            {isTM ? 'My trip schedule' : 'Sync your bookings'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {isTM
              ? 'Only the trips you are assigned to. Updates automatically.'
              : 'Every booking in your dashboard. Updates automatically.'}
          </p>

          <div className="flex gap-2 mb-4">
            <Button
              onClick={handleSubscribeToFeed}
              size="sm"
              style={{ backgroundColor: '#2563EB' }}
              className="hover:opacity-90 text-white"
              data-testid="calendar-subscribe-btn"
            >
              <Calendar className="w-3 h-3 mr-1.5" />
              Subscribe
            </Button>
            <Button
              onClick={handleCopyFeedUrl}
              variant="outline"
              size="sm"
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
              data-testid="calendar-copy-url-btn"
            >
              {copySuccess ? <CheckCircle className="w-3 h-3 mr-1.5" /> : <Copy className="w-3 h-3 mr-1.5" />}
              {copySuccess ? 'Copied' : 'Copy URL'}
            </Button>
          </div>

          <div className="text-xs text-gray-500 space-y-1 border-t border-gray-100 pt-3">
            <p><span className="font-medium text-gray-700">Apple Calendar:</span> Click Subscribe — opens automatically</p>
            <p><span className="font-medium text-gray-700">Google:</span> Copy URL → Other calendars → From URL → paste</p>
            <p><span className="font-medium text-gray-700">Outlook:</span> Copy URL → Add calendar → Subscribe from web</p>
          </div>
        </div>
      </div>
    );
  }

  // WEBHOOKS TAB
  if (activeSubView === 'settings-webhooks') {
    return (
      <div className="space-y-4">
        <SettingsHeader
          title="Webhooks"
          subtitle="Receive HTTPS POSTs in real time when bookings, payments, or refunds change."
        >
          <Button
            onClick={() => setShowNewWebhookModal(true)}
            size="sm"
            style={{ backgroundColor: '#2563EB' }}
            className="hover:opacity-90 h-8 text-white"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Webhook
          </Button>
        </SettingsHeader>

        {/* Webhook List */}
        {loadingWebhooks ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : webhooks.length === 0 ? (
          <div className="settings-card p-10 text-center">
            <Webhook className="w-10 h-10 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">No webhooks configured</p>
            <p className="text-xs text-gray-500 mt-1">Add your first to start receiving event notifications</p>
          </div>
        ) : (
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <div key={webhook.id} className="settings-card p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md border ${
                        webhook.isActive
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 bg-gray-50 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${webhook.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {webhook.isActive ? 'Active' : 'Inactive'}
                      </span>
                      <button
                        onClick={() => handleToggleWebhook(webhook.id, webhook.isActive)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        {webhook.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                    
                    <p className="text-sm font-mono text-gray-900 break-all mb-2">
                      {webhook.url}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-2">
                      {webhook.events.map((event) => (
                        <span
                          key={event}
                          className="text-[11px] font-mono px-2 py-0.5 rounded-md border border-gray-200 text-gray-700 bg-white"
                        >
                          {event}
                        </span>
                      ))}
                    </div>
                    
                    <p className="text-xs text-gray-500">
                      Created {new Date(webhook.createdAt).toLocaleDateString()}
                      {webhook.lastTriggeredAt && (
                        <span> • Last triggered {new Date(webhook.lastTriggeredAt).toLocaleString()}</span>
                      )}
                    </p>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleTestWebhook(webhook.id)}
                      disabled={testingWebhook === webhook.id || !webhook.isActive}
                      variant="outline"
                      size="sm"
                      className="h-8"
                    >
                      {testingWebhook === webhook.id ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          Testing...
                        </>
                      ) : (
                        'Test'
                      )}
                    </Button>
                    <Button
                      onClick={() => handleDeleteWebhook(webhook.id)}
                      variant="outline"
                      size="sm"
                      className="h-8 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Available Events */}
        <div className="settings-card p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Available events</h3>
          <div className="space-y-1.5">
            {[
              ['booking.created', 'New booking received'],
              ['booking.updated', 'Booking details changed'],
              ['booking.cancelled', 'Booking cancelled'],
            ].map(([evt, desc]) => (
              <div key={evt} className="flex items-center justify-between text-sm">
                <span className="font-mono text-[12px] px-2 py-0.5 rounded-md border border-gray-200 text-gray-700">{evt}</span>
                <span className="text-xs text-gray-500">{desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Payload Example */}
        <div className="settings-card p-4">
          <details>
            <summary className="text-sm font-medium text-gray-900 cursor-pointer select-none flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-600" />
              Sample payload
            </summary>
            <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto mt-3">
{`{
  "event": "booking.created",
  "timestamp": "2026-01-17T10:30:00Z",
  "data": {
    "id": "bkg_abc123",
    "confirmationCode": "ABC123",
    "customerName": "John Doe",
    "customerEmail": "john@example.com",
    "serviceName": "Sunset Cruise",
    "date": "2026-02-15",
    "timeSlot": "6:00 PM - 8:00 PM",
    "quantity": 2,
    "totalPrice": 150.00,
    "paymentStatus": "paid"
  }
}`}
            </pre>
          </details>
        </div>

        {/* New Webhook Modal */}
        <Dialog open={showNewWebhookModal} onOpenChange={setShowNewWebhookModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Webhook</DialogTitle>
              <DialogDescription>
                Enter the URL where you want to receive event notifications
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-sm">Webhook URL <span className="text-red-500">*</span></Label>
                <Input
                  type="url"
                  value={newWebhookUrl}
                  onChange={(e) => setNewWebhookUrl(e.target.value)}
                  placeholder="https://your-server.com/webhook"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Need a test URL? Use <a href="https://webhook.site" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">webhook.site</a> to get a free endpoint
                </p>
              </div>
              
              <div>
                <Label className="text-sm mb-2 block">Events to Subscribe <span className="text-red-500">*</span></Label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhookEvents['booking.created']}
                      onChange={(e) => setNewWebhookEvents({
                        ...newWebhookEvents,
                        'booking.created': e.target.checked
                      })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Booking Created</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhookEvents['booking.updated']}
                      onChange={(e) => setNewWebhookEvents({
                        ...newWebhookEvents,
                        'booking.updated': e.target.checked
                      })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Booking Updated</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newWebhookEvents['booking.cancelled']}
                      onChange={(e) => setNewWebhookEvents({
                        ...newWebhookEvents,
                        'booking.cancelled': e.target.checked
                      })}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Booking Cancelled</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowNewWebhookModal(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateWebhook}
                disabled={saving}
                style={{ backgroundColor: '#2563EB' }}
                className="hover:opacity-90 text-white"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Webhook'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ============ INTEGRATIONS TAB ============
  if (activeSubView === 'settings-integrations') {
    const IntegrationsTab = () => {
      const [selectedIntegration, setSelectedIntegration] = useState(null);
      const [services, setServices] = useState([]);
      const [loading, setLoading] = useState(false);
      const [gygEnabled, setGygEnabled] = useState(false);
      const orgSlug = localStorage.getItem('organizationSlug') || '';
      
      // Viator state
      const [viatorStatus, setViatorStatus] = useState(null);
      const [viatorSupplierId, setViatorSupplierId] = useState('');
      const [viatorContactEmail, setViatorContactEmail] = useState('');
      const [viatorNotes, setViatorNotes] = useState('');
      const [submittingViator, setSubmittingViator] = useState(false);
      
      // LetsBatch state
      const [letsBatchStatus, setLetsBatchStatus] = useState(null);
      const [letsBatchInterested, setLetsBatchInterested] = useState(false);
      const [submittingLetsBatch, setSubmittingLetsBatch] = useState(false);
      
      // Fetch services when GYG is selected
      useEffect(() => {
        if (selectedIntegration === 'gyg') {
          const fetchServices = async () => {
            setLoading(true);
            try {
              const response = await apiV2.get('/services');
              setServices(response.data || []);
              // Check if GYG is enabled (has any service)
              setGygEnabled(response.data?.length > 0);
            } catch (err) {
              console.error('Failed to fetch services:', err);
            } finally {
              setLoading(false);
            }
          };
          fetchServices();
        }
      }, [selectedIntegration]);
      
      // Fetch Viator status when Viator is selected
      useEffect(() => {
        if (selectedIntegration === 'viator') {
          const fetchViatorStatus = async () => {
            setLoading(true);
            try {
              const response = await apiV2.get('/integrations/viator/status');
              setViatorStatus(response.data);
              if (response.data.viatorSupplierId) {
                setViatorSupplierId(response.data.viatorSupplierId);
              }
              if (response.data.contactEmail) {
                setViatorContactEmail(response.data.contactEmail);
              }
            } catch (err) {
              console.error('Failed to fetch Viator status:', err);
            } finally {
              setLoading(false);
            }
          };
          fetchViatorStatus();
        }
        
        // Fetch LetsBatch status when LetsBatch is selected
        if (selectedIntegration === 'letsbatch') {
          const fetchLetsBatchStatus = async () => {
            setLoading(true);
            try {
              const response = await apiV2.get('/integrations/letsbatch/status');
              setLetsBatchStatus(response.data);
              setLetsBatchInterested(response.data.hasAccount || false);
            } catch (err) {
              console.error('Failed to fetch LetsBatch status:', err);
            } finally {
              setLoading(false);
            }
          };
          fetchLetsBatchStatus();
        }
      }, [selectedIntegration]);
      
      // Submit Viator interest
      const handleViatorSubmit = async () => {
        if (!viatorSupplierId.trim()) {
          toast.error('Please enter your Viator Supplier ID');
          return;
        }
        
        setSubmittingViator(true);
        try {
          const response = await apiV2.post('/integrations/viator/register-interest', {
            viatorSupplierId: viatorSupplierId.trim(),
            contactEmail: viatorContactEmail.trim(),
            notes: viatorNotes.trim()
          });
          
          toast.success(response.data.message || 'Registration successful!');
          setViatorStatus({
            registered: true,
            status: response.data.status || 'pending',
            viatorSupplierId: viatorSupplierId,
            message: response.data.message
          });
        } catch (err) {
          console.error('Failed to register Viator interest:', err);
          toast.error(err.response?.data?.detail || 'Failed to register');
        } finally {
          setSubmittingViator(false);
        }
      };
      
      const copyToClipboard = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied!`);
      };
      
      // GYG Detail View
      if (selectedIntegration === 'gyg') {
        return (
          <div className="space-y-6">
            {/* Header with back button */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSelectedIntegration(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-xl font-bold text-gray-900">GetYourGuide Integration</h2>
                <p className="text-sm text-gray-600">Connect your services to GYG</p>
              </div>
            </div>
            
            {/* Connection Status */}
            <div className={`p-4 rounded-xl border ${gygEnabled ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${gygEnabled ? 'bg-green-500' : 'bg-orange-500'}`} />
                <span className={`font-medium ${gygEnabled ? 'text-green-800' : 'text-orange-800'}`}>
                  {gygEnabled ? 'Integration Active' : 'Ready to Connect'}
                </span>
              </div>
              <p className="text-sm text-gray-600 mt-1 ml-6">
                {gygEnabled 
                  ? 'GYG bookings will appear in your dashboard automatically'
                  : 'Follow the steps below to connect with GetYourGuide'}
              </p>
            </div>
            
            {/* API Endpoint Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
              <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                <Code className="w-4 h-4" />
                Your GYG API Endpoint
              </h3>
              <p className="text-sm text-blue-800 mb-3">
                Provide this URL to GetYourGuide when setting up your Connectivity Partner integration:
              </p>
              <div className="flex items-center gap-2 bg-white p-3 rounded-lg border border-blue-200">
                <code className="flex-1 text-sm font-mono text-gray-800 break-all">
                  {window.location.origin.replace('http://', 'https://').replace(':3000', '')}/api/gyg
                </code>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => copyToClipboard(`${window.location.origin.replace('http://', 'https://').replace(':3000', '')}/api/gyg`, 'API Endpoint')}
                  className="flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Contact <a href="mailto:ryan@bookingflows.com" className="underline">ryan@bookingflows.com</a> to get your API credentials
              </p>
            </div>
            
            {/* Setup Steps */}
            <div className="bg-white border border-gray-300 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-4">How to Connect</h3>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center flex-shrink-0">1</div>
                  <div>
                    <p className="font-medium text-gray-900">Apply to GYG Connectivity Program</p>
                    <p className="text-sm text-gray-600">Sign up at <a href="https://supplier.getyourguide.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">supplier.getyourguide.com</a></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center flex-shrink-0">2</div>
                  <div>
                    <p className="font-medium text-gray-900">Request API Integration</p>
                    <p className="text-sm text-gray-600">Tell GYG you want to connect via their Supplier API</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center flex-shrink-0">3</div>
                  <div>
                    <p className="font-medium text-gray-900">Share Your API Endpoint</p>
                    <p className="text-sm text-gray-600">Provide the endpoint URL above to GYG's integration team</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center flex-shrink-0">4</div>
                  <div>
                    <p className="font-medium text-gray-900">Map Your Products</p>
                    <p className="text-sm text-gray-600">Use the Product IDs below when configuring your tours in GYG</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Supplier ID */}
            <div className="bg-white border border-gray-300 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Your Supplier ID</h3>
              <div className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg border">
                <code className="flex-1 text-sm font-mono text-gray-800">{orgSlug}</code>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => copyToClipboard(orgSlug, 'Supplier ID')}
                  className="flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Products List */}
            <div className="bg-white border border-gray-300 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Your Products</h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : services.length === 0 ? (
                <p className="text-sm text-gray-500 py-4 text-center">
                  No services found. Create services to sync with GYG.
                </p>
              ) : (
                <div className="space-y-3">
                  {services.map((service) => {
                    const productId = `${orgSlug}:${service.id}`;
                    return (
                      <div key={service.id} className="bg-gray-50 p-3 rounded-lg border">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{service.name}</p>
                            <code className="text-xs font-mono text-gray-600 break-all">{productId}</code>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => copyToClipboard(productId, 'Product ID')}
                            className="flex-shrink-0"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Help Link */}
            <p className="text-sm text-gray-500 text-center">
              Need help? Contact <a href="mailto:ryan@bookingflows.com" className="text-blue-600 hover:underline">ryan@bookingflows.com</a>
            </p>
          </div>
        );
      }
      
      // Viator Detail View - Simple contact
      if (selectedIntegration === 'viator') {
        return (
          <div className="space-y-6">
            {/* Header with back button */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSelectedIntegration(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-white">
                  <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Viator Integration</h2>
                  <p className="text-sm text-gray-600">Connect to Viator's global marketplace</p>
                </div>
              </div>
            </div>
            
            {/* Simple Contact Card */}
            <div className="border border-gray-200 rounded-xl p-6 bg-white text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-50 flex items-center justify-center">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Connected with Viator</h3>
              <p className="text-sm text-gray-600 mb-6">
                To set up your Viator integration, reach out to us directly. Make sure to have your <strong>Viator Supplier ID</strong> ready.
              </p>
              
              <a 
                href="mailto:ryan@bookingflows.com?subject=Viator Integration Request&body=Hi Ryan,%0D%0A%0D%0AI'd like to connect my BookingFlows account with Viator.%0D%0A%0D%0AMy Viator Supplier ID: [YOUR_SUPPLIER_ID]%0D%0A%0D%0AThanks!"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email ryan@bookingflows.com
              </a>
              
              <p className="text-xs text-gray-500 mt-4">
                Don't have a Viator Supplier ID? <a href="https://supplier.viator.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Sign up at supplier.viator.com</a>
              </p>
            </div>
          </div>
        );
      }
      
      // LetsBatch Detail View - Simple contact
      if (selectedIntegration === 'letsbatch') {
        return (
          <div className="space-y-6">
            {/* Header with back button */}
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSelectedIntegration(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-black p-1">
                  <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">LetsBatch Integration</h2>
                  <p className="text-sm text-gray-600">Direct traffic model</p>
                </div>
              </div>
            </div>
            
            {/* Simple Contact Card */}
            <div className="border border-gray-200 rounded-xl p-6 bg-white text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <Mail className="w-8 h-8 text-gray-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Connected with LetsBatch</h3>
              <p className="text-sm text-gray-600 mb-6">
                To set up your LetsBatch integration with our direct traffic model, reach out to us directly.
              </p>
              
              <a 
                href="mailto:ryan@bookingflows.com?subject=LetsBatch Integration Request&body=Hi Ryan,%0D%0A%0D%0AI'd like to connect my BookingFlows account with LetsBatch.%0D%0A%0D%0AThanks!"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email ryan@bookingflows.com
              </a>
              
              <p className="text-xs text-gray-500 mt-4">
                Learn more at <a href="https://letsbatch.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">letsbatch.com</a>
              </p>
            </div>
          </div>
        );
      }
      
      // Main Integrations List
      return (
        <div className="space-y-4">
          <SettingsHeader
            title="Integrations"
            subtitle="Connect third-party marketplaces and services to expand your reach."
          />

          {/* GetYourGuide - Clickable */}
          <div 
            className="bg-white border border-gray-300 rounded-xl p-6 cursor-pointer hover:border-orange-400 hover:shadow-sm transition-all"
            onClick={() => setSelectedIntegration('gyg')}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                <img src="/images/integrations/gyg-icon.jpg" alt="GetYourGuide" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">GetYourGuide</h3>
                  <Badge className="text-xs bg-green-600 text-white">Available</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  List your tours and activities on GetYourGuide to reach millions of travelers worldwide.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* Viator - Clickable */}
          <div 
            className="bg-white border border-gray-300 rounded-xl p-6 cursor-pointer hover:border-green-400 hover:shadow-sm transition-all"
            onClick={() => setSelectedIntegration('viator')}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-white">
                <img src="/images/integrations/viator-icon.png" alt="Viator" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">Viator</h3>
                  <Badge className="text-xs bg-green-600 text-white">Available</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Expand your bookings through Viator's global travel marketplace.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>

          {/* LetsBatch - Clickable */}
          <div 
            className="bg-white border border-gray-300 rounded-xl p-6 cursor-pointer hover:border-gray-600 hover:shadow-sm transition-all"
            onClick={() => setSelectedIntegration('letsbatch')}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-black p-1">
                <img src="/images/integrations/letsbatch-icon.png" alt="LetsBatch" className="w-full h-full object-contain" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">LetsBatch</h3>
                  <Badge className="text-xs bg-gray-800 text-white">Available</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Connect with LetsBatch for direct traffic bookings.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>
      );
    };
    
    return <IntegrationsTab />;
  }

  // ============ 2FA SECURITY TAB ============
  if (activeSubView === 'settings-2fa') {

    const PasskeySection = () => {
      const [passkeys, setPasskeys] = useState([]);
      const [registering, setRegistering] = useState(false);
      const [supported, setSupported] = useState(false);
      const API_URL = process.env.REACT_APP_BACKEND_URL;

      useEffect(() => {
        if (window.PublicKeyCredential &&
          typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function') {
          window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
            .then(ok => setSupported(ok)).catch(() => {});
        }
        apiV2.get('/auth/webauthn/credentials').then(r => setPasskeys(r.data.credentials || [])).catch(() => {});
      }, []);

      const handleRegister = async () => {
        setRegistering(true);
        try {
          const token = localStorage.getItem('jwtToken');
          // Use axios (XHR) to bypass service worker fetch interception
          const _ax = (await import('axios')).default;
          const ax = _ax.create({ baseURL: API_URL, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });

          const options = (await ax.post('/api/v2/auth/webauthn/register-options', { origin: window.location.origin })).data;
          const parseB64 = s => { const b = s.replace(/-/g,'+').replace(/_/g,'/'); const bin = atob(b.padEnd(b.length+(4-b.length%4)%4,'=')); return Uint8Array.from(bin, c=>c.charCodeAt(0)); };
          const pko = {
            challenge: parseB64(options.challenge),
            rp: { id: options.rp?.id, name: options.rp?.name },
            user: { id: parseB64(options.user?.id), name: options.user?.name, displayName: options.user?.displayName },
            pubKeyCredParams: (options.pubKeyCredParams||[]).map(p=>({type:p.type,alg:p.alg})),
            timeout: options.timeout || 60000,
            excludeCredentials: (options.excludeCredentials||[]).map(c=>({type:'public-key',id:parseB64(c.id)})),
            authenticatorSelection: { authenticatorAttachment:'platform', residentKey:'preferred', userVerification:'preferred' },
            attestation: 'none',
          };
          const cred = await navigator.credentials.create({ publicKey: pko });
          const toB64 = buf => { const b = new Uint8Array(buf); let s=''; b.forEach(x=>s+=String.fromCharCode(x)); return btoa(s).replace(/\+/g,'-').replace(/\//g,'_').replace(/=/g,''); };
          const dn = /iPhone/.test(navigator.userAgent)?'iPhone':/iPad/.test(navigator.userAgent)?'iPad':/Mac/.test(navigator.userAgent)?'Mac':/Android/.test(navigator.userAgent)?'Android':'This device';
          await ax.post('/api/v2/auth/webauthn/register', { id:cred.id, rawId:toB64(cred.rawId), type:cred.type, response:{ attestationObject:toB64(cred.response.attestationObject), clientDataJSON:toB64(cred.response.clientDataJSON) }, deviceName:dn, origin:window.location.origin });
          toast.success('Passkey registered! Sign in with Face ID / Touch ID next time.');
          const refresh = await apiV2.get('/auth/webauthn/credentials');
          setPasskeys(refresh.data.credentials || []);
        } catch (err) {
          if (err.name === 'NotAllowedError') toast.error('Registration cancelled');
          else toast.error(err.message || 'Failed to register passkey');
        } finally {
          setRegistering(false); }
      };

      const handleDelete = async (credId) => {
        await apiV2.delete(`/auth/webauthn/credentials/${credId}`);
        setPasskeys(p => p.filter(c => c.id !== credId));
        toast.success('Passkey removed');
      };

      if (!supported) return (
        <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500">
          Face ID / Touch ID passkeys are not available on this device or browser.
          <p className="mt-1 text-xs">Supported on: Safari (iPhone/Mac), Chrome (Android), Edge (Windows)</p>
        </div>
      );

      return (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-gray-900 text-sm">Face ID / Touch ID Login</h4>
              <p className="text-xs text-gray-500 mt-0.5">Sign in instantly without a password</p>
            </div>
            <button onClick={handleRegister} disabled={registering}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-medium disabled:opacity-50">
              {registering ? 'Setting up...' : '+ Add Passkey'}
            </button>
          </div>
          {passkeys.length > 0 ? (
            <div className="space-y-2">
              {passkeys.map(pk => (
                <div key={pk.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{pk.deviceName || 'Device'}</p>
                    <p className="text-xs text-gray-400">Added {pk.createdAt ? new Date(pk.createdAt).toLocaleDateString() : ''}</p>
                  </div>
                  <button onClick={() => handleDelete(pk.id)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400">No passkeys set up yet. Add one to enable Face ID / Touch ID login.</p>
          )}
        </div>
      );
    };


    const TwoFASettings = () => {
      const [twoFAEnabled, setTwoFAEnabled] = useState(false);
      const [twoFAMethod, setTwoFAMethod] = useState('email');
      const [loading, setLoading] = useState(true);
      const [enabling, setEnabling] = useState(false);
      
      // Fetch current 2FA settings
      const fetch2FASettings = async () => {
        try {
          setLoading(true);
          const response = await apiV2.get('/auth/2fa/settings');
          setTwoFAEnabled(response.data.enabled);
          setTwoFAMethod(response.data.method || 'email');
        } catch (err) {
          console.error('Failed to fetch 2FA settings:', err);
          toast.error('Failed to load 2FA settings');
        } finally {
          setLoading(false);
        }
      };
      
      useEffect(() => {
        fetch2FASettings();
      }, []);
      
      const handleEnable2FA = async () => {
        setEnabling(true);
        try {
          await apiV2.post('/auth/2fa/enable', { method: twoFAMethod });
          setTwoFAEnabled(true);
          toast.success('Two-factor authentication enabled! You will need to enter a code on your next login.');
          // Refetch to confirm
          await fetch2FASettings();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to enable 2FA');
        } finally {
          setEnabling(false);
        }
      };
      
      const handleDisable2FA = async () => {
        if (!window.confirm('Are you sure you want to disable two-factor authentication? This will reduce your account security.')) {
          return;
        }
        
        setEnabling(true);
        try {
          await apiV2.post('/auth/2fa/disable', {});
          setTwoFAEnabled(false);
          toast.success('Two-factor authentication disabled');
          // Refetch to confirm
          await fetch2FASettings();
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to disable 2FA');
        } finally {
          setEnabling(false);
        }
      };
      
      if (loading) {
        return (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        );
      }
      
      return (
        <div className="space-y-4">
          <SettingsHeader
            title="Two-Factor Authentication"
            subtitle="Require a verification code when signing in."
          />

          {/* 2FA Status Card */}
          <div className="settings-card p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${twoFAEnabled ? 'bg-emerald-50' : 'bg-gray-100'}`}>
                  <Shield className={`w-5 h-5 ${twoFAEnabled ? 'text-emerald-600' : 'text-gray-500'}`} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">2FA Protection</h3>
                  <p className="text-xs text-gray-500">
                    {twoFAEnabled ? 'Active on this account' : 'Not enabled'}
                  </p>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-md border ${
                twoFAEnabled
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-gray-200 bg-gray-50 text-gray-600'
              }`}>
                <span className={`w-1.5 h-1.5 rounded-full ${twoFAEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                {twoFAEnabled ? 'On' : 'Off'}
              </span>
            </div>

            {!twoFAEnabled ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  You'll receive a 6-digit code via email when signing in.
                </p>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white h-9"
                  onClick={handleEnable2FA}
                  disabled={enabling}
                  data-testid="enable-2fa-btn"
                >
                  {enabling ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enabling...</>
                  ) : (
                    <><Shield className="w-4 h-4 mr-2" /> Enable 2FA</>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Verification codes are sent via email on each sign-in.
                </p>
                <Button
                  variant="outline"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-300 h-9"
                  onClick={handleDisable2FA}
                  disabled={enabling}
                  data-testid="disable-2fa-btn"
                >
                  {enabling ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Disabling...</>
                  ) : (
                    'Disable 2FA'
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      );
    };
    
    return <TwoFASettings />;
  }

  // ============ DANGER ZONE TAB ============
  if (activeSubView === 'settings-danger') {
    // Danger Zone component with multi-step deletion flow
    const DangerZone = () => {
      const [step, setStep] = useState(0); // 0: initial, 1: type delete, 2: summary, 3: type yes, 4: enter code
      const [deleteInput, setDeleteInput] = useState('');
      const [yesInput, setYesInput] = useState('');
      const [verificationCode, setVerificationCode] = useState('');
      const [deletionSummary, setDeletionSummary] = useState(null);
      const [processing, setProcessing] = useState(false);
      const [error, setError] = useState(null);

      const [blockedReason, setBlockedReason] = useState(null);

      const handleInitiateDelete = async () => {
        if (deleteInput.toLowerCase() !== 'delete') {
          setError('Type "delete" exactly to proceed');
          return;
        }
        try {
          setProcessing(true);
          setError(null);
          setBlockedReason(null);
          const response = await apiV2.post('/auth/organizations/delete/initiate', {
            confirmationText: deleteInput,
          });
          setDeletionSummary(response.data.summary);
          setStep(2);
        } catch (err) {
          const detail = err.response?.data?.detail;
          // 409 future_bookings_exist — show the block screen
          if (typeof detail === 'object' && detail?.code === 'future_bookings_exist') {
            setBlockedReason(detail);
            setStep(99);
          } else {
            setError(typeof detail === 'string' ? detail : 'Failed to initiate deletion');
          }
        } finally {
          setProcessing(false);
        }
      };

      const handleConfirmDelete = async () => {
        if (yesInput.toLowerCase() !== 'yes') {
          setError('Type "yes" exactly to confirm');
          return;
        }
        if (!verificationCode || verificationCode.length !== 6) {
          setError('Enter the 6-digit code we emailed you');
          return;
        }
        try {
          setProcessing(true);
          setError(null);
          await apiV2.post('/auth/organizations/delete/confirm', {
            verificationCode,
            finalConfirmation: yesInput,
          });
          toast.success('Organization deleted');
          localStorage.clear();
          window.location.href = '/login';
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to delete organization');
        } finally {
          setProcessing(false);
        }
      };

      const resetFlow = () => {
        setStep(0);
        setDeleteInput('');
        setYesInput('');
        setVerificationCode('');
        setDeletionSummary(null);
        setBlockedReason(null);
        setError(null);
      };

      return (
        <div className="space-y-4">
          <SettingsHeader
            title={<span className="text-red-600 inline-flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Danger Zone</span>}
            subtitle="Irreversible. Soft-deleted orgs are retained for 90 days, then purged."
          />

          {/* Step 99: Hard-block (future bookings exist) */}
          {step === 99 && blockedReason && (
            <div className="settings-card p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Can't delete yet</h3>
                  <p className="text-sm text-gray-600 mt-1">{blockedReason.message}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm flex items-center justify-between">
                <span className="text-gray-700">
                  <strong>{blockedReason.activeBookings}</strong> upcoming · <strong>${blockedReason.totalRefundAmount?.toFixed(2)}</strong> to refund
                </span>
                <a
                  href={blockedReason.stripeDashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  Open Stripe Dashboard →
                </a>
              </div>
              <Button onClick={resetFlow} variant="outline" className="w-full" data-testid="danger-back-btn">
                Back
              </Button>
            </div>
          )}

          {/* Step 0: Resting state */}
          {step === 0 && (
            <div className="settings-card p-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Delete Organization</h3>
              <p className="text-sm text-gray-600 mb-4">
                Removes services, bookings, team members, customer data, and access to your dashboard.
                Your Stripe account stays yours.
              </p>
              <Button
                onClick={() => setStep(1)}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                data-testid="danger-delete-org-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                I want to delete my organization
              </Button>
            </div>
          )}

          {/* Step 1: Type "delete" */}
          {step === 1 && (
            <div className="settings-card p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Confirm your intent</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Type <span className="font-mono font-semibold text-red-600">delete</span> to continue.
                </p>
              </div>
              <Input
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder='Type "delete"'
                className="font-mono"
                disabled={processing}
                data-testid="danger-delete-input"
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <Button onClick={resetFlow} variant="outline" className="flex-1" disabled={processing}>Cancel</Button>
                <Button
                  onClick={handleInitiateDelete}
                  disabled={processing || deleteInput.toLowerCase() !== 'delete'}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="danger-continue-btn"
                >
                  {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing</> : 'Continue'}
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Summary + PIN sent */}
          {step === 2 && deletionSummary && (
            <div className="settings-card p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">What gets removed</h3>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-semibold text-gray-900">{deletionSummary.services || 0}</div>
                  <div className="text-xs text-gray-500">Services</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <div className="text-xl font-semibold text-gray-900">{deletionSummary.teamMembers || 0}</div>
                  <div className="text-xs text-gray-500">Team Members</div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-blue-50 rounded-lg p-3">
                <Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <span>We emailed a 6-digit code to the owner. Expires in 15 minutes.</span>
              </div>
              <div className="flex gap-2">
                <Button onClick={resetFlow} variant="outline" className="flex-1">Cancel</Button>
                <Button
                  onClick={() => setStep(3)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="danger-summary-continue"
                >
                  Enter code
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: PIN + "yes" */}
          {step === 3 && (
            <div className="settings-card p-6 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Final confirmation</h3>
                <p className="text-sm text-gray-600 mt-1">This cannot be undone.</p>
              </div>
              <div>
                <Label className="text-xs text-gray-500">6-digit code</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="font-mono text-lg tracking-[0.5em] text-center mt-1"
                  maxLength={6}
                  disabled={processing}
                  data-testid="danger-pin-input"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500">
                  Type <span className="font-mono font-semibold text-red-600">yes</span> to permanently delete
                </Label>
                <Input
                  value={yesInput}
                  onChange={(e) => setYesInput(e.target.value)}
                  placeholder='Type "yes"'
                  className="font-mono mt-1"
                  disabled={processing}
                  data-testid="danger-yes-input"
                />
              </div>
              {error && <p className="text-sm text-red-600">{error}</p>}
              <div className="flex gap-2">
                <Button onClick={resetFlow} variant="outline" className="flex-1" disabled={processing}>Cancel</Button>
                <Button
                  onClick={handleConfirmDelete}
                  disabled={processing || yesInput.toLowerCase() !== 'yes' || verificationCode.length !== 6}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  data-testid="danger-delete-forever"
                >
                  {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting</> : <><Trash2 className="w-4 h-4 mr-2" />Delete forever</>}
                </Button>
              </div>
            </div>
          )}
        </div>
      );
    };

    return <DangerZone />;
  }

  return null;
};

// ============================================================================
// CHANGE PASSWORD MODAL COMPONENT
// ============================================================================
const ChangePasswordModal = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);

  const isPasswordValid = 
    newPassword.length >= 8 &&
    /[a-z]/.test(newPassword) &&
    /[A-Z]/.test(newPassword) &&
    /[0-9]/.test(newPassword);

  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await apiV2.post('/auth/account/change-password', {
        currentPassword,
        newPassword
      });
      
      toast.success('Password changed successfully!');
      onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to change password';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-blue-600" />
            Change Password
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Current Password *</Label>
            <Input
              type={showPasswords ? "text" : "password"}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter current password"
              disabled={loading}
              data-testid="current-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">New Password *</Label>
            <Input
              type={showPasswords ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              disabled={loading}
              data-testid="new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Confirm New Password *</Label>
            <Input
              type={showPasswords ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              disabled={loading}
              data-testid="confirm-password-input"
            />
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-500">Passwords do not match</p>
            )}
            {passwordsMatch && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <Check className="w-3 h-3" /> Passwords match
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
            <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
            <div className="flex items-center gap-2">
              {newPassword.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
              <span className={`text-xs ${newPassword.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
            </div>
            <div className="flex items-center gap-2">
              {/[a-z]/.test(newPassword) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
              <span className={`text-xs ${/[a-z]/.test(newPassword) ? 'text-green-700' : 'text-gray-600'}`}>1 lowercase letter</span>
            </div>
            <div className="flex items-center gap-2">
              {/[A-Z]/.test(newPassword) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
              <span className={`text-xs ${/[A-Z]/.test(newPassword) ? 'text-green-700' : 'text-gray-600'}`}>1 uppercase letter</span>
            </div>
            <div className="flex items-center gap-2">
              {/[0-9]/.test(newPassword) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
              <span className={`text-xs ${/[0-9]/.test(newPassword) ? 'text-green-700' : 'text-gray-600'}`}>1 number</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showPasswords"
              checked={showPasswords}
              onChange={() => setShowPasswords(!showPasswords)}
              className="rounded border-gray-300"
            />
            <label htmlFor="showPasswords" className="text-sm text-gray-600">
              Show passwords
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isPasswordValid || !passwordsMatch || !currentPassword}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="change-password-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Changing...
                </span>
              ) : (
                "Change Password"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================================
// CHANGE EMAIL MODAL COMPONENT
// ============================================================================
const ChangeEmailModal = ({ currentEmail, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail);
  const isDifferentEmail = newEmail.toLowerCase() !== currentEmail?.toLowerCase();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error('Please enter your current password');
      return;
    }
    
    if (!isValidEmail) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (!isDifferentEmail) {
      toast.error('New email must be different from current email');
      return;
    }

    setLoading(true);
    try {
      const response = await apiV2.post('/auth/account/request-email-change', {
        currentPassword: password,
        newEmail
      });
      
      // Show success message - don't auto-open URL as it would verify immediately
      toast.success(response.data.message || 'Verification email sent! Check your new inbox.');
      
      // If email failed to send but we have a URL, try to copy it to clipboard
      if (response.data.verifyUrl && response.data.error) {
        try {
          await navigator.clipboard?.writeText(response.data.verifyUrl);
          toast.info('Verification link copied to clipboard. Open it in a new tab to verify.');
        } catch {
          // Clipboard API may not be available - show URL in console
          toast.info('Check browser console for verification link.');
        }
      }
      
      onSuccess();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to initiate email change';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Change Email
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">Current Email</Label>
            <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600 text-sm">
              {currentEmail}
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm">New Email Address *</Label>
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
              disabled={loading}
              data-testid="new-email-input"
            />
            {newEmail && !isValidEmail && (
              <p className="text-xs text-red-500">Please enter a valid email address</p>
            )}
            {newEmail && isValidEmail && !isDifferentEmail && (
              <p className="text-xs text-amber-600">New email must be different from current</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-sm">Current Password *</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password to confirm"
              disabled={loading}
              data-testid="email-change-password-input"
            />
          </div>

          <p className="text-xs text-gray-500">
            A verification link will be sent to your new email. Your current email ({currentEmail}) will be notified of this change.
          </p>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !isValidEmail || !isDifferentEmail || !password}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="change-email-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </span>
              ) : (
                "Send Verification"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsTabEnhanced;
